import React, {
  CSSProperties,
  useEffect,
  useRef,
  useState,
  useContext
} from 'react'
import AgoraUIKit, { RtcContext, StylePropInterface } from 'agora-react-uikit'
import AgoraRTM from 'agora-rtm-sdk'
import 'agora-react-uikit/dist/index.css'
import './App.css'
import ChatPanel from './ChatPanel'
import StatusScreen from './StatusScreen'
import logo from './assets/images/logo.png'
import { AiCallRecorder } from './aiCallRecorder'

type RtmClient = ReturnType<typeof AgoraRTM.createInstance>
type RtmChannel = ReturnType<RtmClient['createChannel']>

interface JoinData {
  agora_app_id: string
  token: string
  rtm_token: string
  channel_name: string
  uid: number
  join_url: string
  expires_at: string
  appointment_id?: number
  auth_token?: string
  refresh_token?: string
  session_id?: string | null
  // Optional counterpart platform user ids used to scope the consultation chat room.
  other_user_id?: number
  patient_user_id?: number
  doctor_user_id?: number
  // Participant display names (added to the join link by consultation-service).
  doctor_name?: string
  patient_name?: string
  // Consultation slot length in minutes (15, or 40 for an extended-slot doctor). Absent on
  // links made before it existed, which are 15-minute slots.
  slot_duration_minutes?: number
}

/** Display names of both participants, shown on the video tiles instead of the role. */
interface ParticipantNames {
  doctor?: string
  patient?: string
}

/** First non-empty trimmed string among the candidates. */
const firstNonEmpty = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return undefined
}

/** "full_name surname" from a user-shaped object, when it has either part. */
const composePersonName = (person: any): string | undefined => {
  if (!person || typeof person !== 'object') return undefined
  return firstNonEmpty(
    [person.full_name, person.surname]
      .filter((part) => typeof part === 'string' && part.trim())
      .map((part: string) => part.trim())
      .join(' '),
    person.name,
    person.display_name
  )
}

/**
 * Fallback for links created before the backend added names: read them from the appointment
 * detail the app already fetches (doctor or patient endpoint — both shapes are probed).
 */
const extractParticipantNames = (appointment: any): ParticipantNames => {
  if (!appointment || typeof appointment !== 'object') return {}
  return {
    doctor: firstNonEmpty(
      appointment.doctor_name,
      composePersonName(appointment.doctor),
      composePersonName(appointment.raw?.doctor)
    ),
    patient: firstNonEmpty(
      appointment.patient_name,
      composePersonName(appointment.patient),
      composePersonName(appointment.raw?.patient)
    )
  }
}

/** Read a value from this tab's sessionStorage first, then localStorage. */
const readStoredValue = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key) || localStorage.getItem(key)
  } catch {
    return null
  }
}

/** Persist fresh auth tokens where the rest of the app looks for them. */
const storeAuthTokens = (accessToken: string, refreshToken?: string) => {
  try {
    for (const storage of [sessionStorage, localStorage]) {
      storage.setItem('access_token', accessToken)
      storage.setItem('auth_token', accessToken)
      if (refreshToken) storage.setItem('refresh_token', refreshToken)
    }
  } catch (error) {
    console.warn('⚠️ Failed to store refreshed tokens:', error)
  }
}

type UserRole = 'doctor' | 'patient'

/**
 * UID the UI kit gives the screen-share client (its default; rtcProps.screenshareUid is unset).
 * The kit already treats this uid as reserved for screen sharing.
 */
const SCREENSHARE_UID = 1
/** While the other participant is disconnected, how often to check whether the call was ended. */
const REMOTE_END_POLL_MS = 5000

/**
 * Length of a consultation slot when the join link does not say (links made before
 * `slot_duration_minutes` existed): the standard 15 minutes.
 */
const DEFAULT_CONSULTATION_DURATION_MS = 15 * 60 * 1000

/**
 * Length of THIS consultation's slot. consultation-service sends `slot_duration_minutes` with
 * the join link (15, or 40 for an extended-slot doctor). The link's `expires_at` is the slot
 * END, i.e. the scheduled time + this duration, so the slot START is `expires_at` minus this.
 */
const consultationDurationMs = (
  data?: Pick<JoinData, 'slot_duration_minutes'> | null
): number => {
  const minutes = Number(data?.slot_duration_minutes)
  return Number.isInteger(minutes) && minutes > 0 && minutes <= 240
    ? minutes * 60 * 1000
    : DEFAULT_CONSULTATION_DURATION_MS
}

/** "mm:ss", or "h:mm:ss" past an hour. */
const formatCountdown = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const mm = minutes.toString().padStart(2, '0')
  const ss = seconds.toString().padStart(2, '0')
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isJoinData = (payload: unknown): payload is JoinData => {
  if (!isRecord(payload)) {
    return false
  }

  return (
    typeof payload.agora_app_id === 'string' &&
    typeof payload.token === 'string' &&
    typeof payload.rtm_token === 'string' &&
    typeof payload.channel_name === 'string' &&
    typeof payload.uid === 'number' &&
    typeof payload.join_url === 'string' &&
    typeof payload.expires_at === 'string'
  )
}

/**
 * Decode JWT token and check if it's expired
 * @param token - JWT token string
 * @returns Object with decoded payload and expiration status
 */
const decodeJWT = (token: string): { payload: any; isExpired: boolean } => {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) {
      return { payload: null, isExpired: true }
    }

    const payload = JSON.parse(decodeBase64Url(parts[1]))
    const currentTime = Math.floor(Date.now() / 1000)
    const isExpired = payload.exp ? payload.exp < currentTime : true

    return { payload, isExpired }
  } catch (error) {
    console.error('Error decoding JWT:', error)
    return { payload: null, isExpired: true }
  }
}

const decodeBase64Url = (encoded: string): string => {
  const normalized = encoded.replace(/-/g, '+').replace(/_/g, '/')
  const padding = normalized.length % 4 === 0 ? 0 : 4 - (normalized.length % 4)
  const padded = normalized.concat('='.repeat(padding))

  if (typeof globalThis.atob === 'function') {
    return globalThis.atob(padded)
  }

  if (typeof Buffer !== 'undefined') {
    return Buffer.from(padded, 'base64').toString('utf-8')
  }

  throw new Error('A Base64 decoder is not available in this environment.')
}

const parseQueryBootstrap = (): {
  joinData: JoinData | null
  username: string | null
  role: UserRole
  error: string | null
} => {
  if (typeof window === 'undefined') {
    return {
      joinData: null,
      username: null,
      role: 'doctor',
      error: null
    }
  }

  try {
    const currentUrl = new URL(window.location.href)
    const params = currentUrl.searchParams
    console.log('params', params)
    const username =
      params.get('name') || params.get('username') || params.get('display_name')
    const roleParam = (params.get('role') || '').toLowerCase()
    console.log('ROLE PARAM: ', roleParam)
    const role: UserRole = roleParam === 'patient' ? 'patient' : 'doctor'

    const credsParam =
      params.get('creds') ||
      params.get('payload') ||
      params.get('session_payload')

    let joinData: JoinData | null = null
    let decodeError: string | null = null

    if (credsParam) {
      try {
        const decodedJson = decodeBase64Url(credsParam)
        const parsed = JSON.parse(decodedJson) as unknown

        if (isJoinData(parsed)) {
          joinData = {
            ...parsed,
            join_url: parsed.join_url || currentUrl.toString(),
            // Extract appointment_id, auth_token, refresh_token, and session_id from parsed data if available
            appointment_id: (parsed as any).appointment_id || undefined,
            auth_token:
              (parsed as any).auth_token ||
              (parsed as any).access_token ||
              undefined,
            refresh_token: (parsed as any).refresh_token || undefined,
            session_id: (parsed as any).session_id || undefined,
            // Participant display names (optional).
            doctor_name: (parsed as any).doctor_name || undefined,
            patient_name: (parsed as any).patient_name || undefined,
            // Counterpart ids for chat room resolution (optional).
            other_user_id: (parsed as any).other_user_id || undefined,
            patient_user_id:
              (parsed as any).patient_user_id ||
              (parsed as any).patient_id ||
              undefined,
            doctor_user_id:
              (parsed as any).doctor_user_id ||
              (parsed as any).doctor_id ||
              undefined
          }
        } else {
          decodeError = 'The meeting credentials in the link were not valid.'
        }
      } catch (error) {
        decodeError =
          error instanceof Error
            ? error.message
            : 'Invalid meeting credentials encoded in this link.'
      }
    }

    if (!joinData) {
      const appId = params.get('agora_app_id')
      const token = params.get('token')
      const rtmToken = params.get('rtm_token')
      const channelName = params.get('channel_name')
      const uidRaw = params.get('uid')
      const expiresAtRaw = params.get('expires_at')
      const appointmentIdRaw = params.get('appointment_id')
      const authToken = params.get('auth_token') || params.get('access_token')
      const refreshToken = params.get('refresh_token')
      const sessionId = params.get('session_id')

      if (appId && token && rtmToken && channelName && uidRaw && expiresAtRaw) {
        // Validate UID format before parsing (should be numeric only)
        const sanitizedUid = uidRaw.trim()
        if (!/^\d+$/.test(sanitizedUid)) {
          decodeError = `The meeting link contains an invalid UID format: "${uidRaw}". Expected numeric value.`
        } else {
          const uid = Number(sanitizedUid)

          if (Number.isNaN(uid) || uid <= 0) {
            decodeError = `The meeting link contains an invalid UID: "${uidRaw}". Must be a positive number.`
          } else {
            joinData = {
              agora_app_id: appId,
              token,
              rtm_token: rtmToken,
              channel_name: channelName,
              uid,
              join_url: currentUrl.toString(),
              expires_at: expiresAtRaw,
              appointment_id: appointmentIdRaw
                ? parseInt(appointmentIdRaw, 10)
                : undefined,
              auth_token: authToken || undefined,
              refresh_token: refreshToken || undefined,
              session_id: sessionId || undefined
            }
          }
        }
      } else if (!decodeError) {
        decodeError = 'Meeting credentials are missing from this link.'
      }
    } else {
      // Extract appointment_id, auth_token, refresh_token, and session_id from URL params if not in joinData
      const appointmentIdRaw = params.get('appointment_id')
      const authToken = params.get('auth_token') || params.get('access_token')
      const refreshToken = params.get('refresh_token')
      const sessionId = params.get('session_id')

      if (appointmentIdRaw && !joinData.appointment_id) {
        joinData.appointment_id = parseInt(appointmentIdRaw, 10)
      }
      if (authToken && !joinData.auth_token) {
        joinData.auth_token = authToken
      }
      if (refreshToken && !joinData.refresh_token) {
        joinData.refresh_token = refreshToken
      }
      if (sessionId && !joinData.session_id) {
        joinData.session_id = sessionId
      }
    }

    if (!joinData) {
      return {
        joinData: null,
        username,
        role,
        error:
          decodeError ??
          'Unable to read the meeting credentials from this link.'
      }
    }

    // Counterpart user ids for chat room resolution may also arrive as plain URL params.
    const otherUserIdRaw = params.get('other_user_id')
    const patientUserIdRaw =
      params.get('patient_user_id') || params.get('patient_id')
    const doctorUserIdRaw =
      params.get('doctor_user_id') || params.get('doctor_id')
    if (otherUserIdRaw && !joinData.other_user_id) {
      joinData.other_user_id = parseInt(otherUserIdRaw, 10)
    }
    if (patientUserIdRaw && !joinData.patient_user_id) {
      joinData.patient_user_id = parseInt(patientUserIdRaw, 10)
    }
    if (doctorUserIdRaw && !joinData.doctor_user_id) {
      joinData.doctor_user_id = parseInt(doctorUserIdRaw, 10)
    }

    // Participant display names for the video tiles (sent by consultation-service).
    const doctorNameParam = firstNonEmpty(params.get('doctor_name'))
    const patientNameParam = firstNonEmpty(params.get('patient_name'))
    if (doctorNameParam && !joinData.doctor_name) {
      joinData.doctor_name = doctorNameParam
    }
    if (patientNameParam && !joinData.patient_name) {
      joinData.patient_name = patientNameParam
    }

    // Consultation slot length (15, or 40 for an extended-slot doctor), sent by
    // consultation-service with the join link. Older links have none and mean 15.
    const slotMinutesParam = Number(params.get('slot_duration_minutes'))
    if (
      !joinData.slot_duration_minutes &&
      Number.isInteger(slotMinutesParam) &&
      slotMinutesParam > 0
    ) {
      joinData.slot_duration_minutes = slotMinutesParam
    }

    const expiresAt = new Date(joinData.expires_at)
    if (Number.isNaN(expiresAt.getTime())) {
      return {
        joinData: null,
        username,
        role,
        error: 'The meeting link has an invalid expiration date.'
      }
    }

    if (expiresAt.getTime() <= Date.now()) {
      return {
        joinData: null,
        username,
        role,
        error:
          'The meeting link has expired. Please refresh the page from the doctor dashboard.'
      }
    }

    return {
      joinData,
      username,
      role,
      error: null
    }
  } catch (err) {
    const message =
      err instanceof Error
        ? err.message
        : 'Failed to decode meeting credentials.'
    return {
      joinData: null,
      username: null,
      role: 'doctor',
      error: message
    }
  }
}

// (Icons removed - using UIKit's functional controls)

const App: React.FunctionComponent = () => {
  const [videocall, setVideocall] = useState(false)
  const [_isHost, setHost] = useState(true)
  const [role, setRole] = useState<UserRole>('doctor')
  const [_username, setUsername] = useState('Doctor')
  const [error, setError] = useState<string | null>(null)
  const [joinDetails, setJoinDetails] = useState<JoinData | null>(null)
  const [participantNames, setParticipantNames] = useState<ParticipantNames>({})
  // Latest join details for async callbacks (token refresh) that outlive a render.
  const joinDetailsRef = useRef<JoinData | null>(null)
  joinDetailsRef.current = joinDetails
  // A single in-flight token refresh shared by every caller (chat reconnects, uploads...).
  const tokenRefreshInFlightRef = useRef<Promise<string | null> | null>(null)
  const [timeRemaining, setTimeRemaining] = useState<string | null>(null)
  // Set while the call was joined before the scheduled start: time left until the slot begins.
  const [timeUntilStart, setTimeUntilStart] = useState<string | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  // Reconnection (§1): true when the other participant appears to have temporarily dropped
  // (left the RTM channel WITHOUT an intentional end-call). The call stays open so they can
  // rejoin until the slot ends; we just surface a non-terminal notice.
  const [remoteDisconnected, setRemoteDisconnected] = useState(false)

  // Use timeRemaining to satisfy TypeScript
  const hasTimeRemaining = Boolean(timeRemaining)
  const stageWrapperRef = useRef<HTMLDivElement | null>(null)
  const previousBodyOverflow = useRef<string | null>(null)
  const hasPinnedRemoteRef = useRef(false)
  const { dispatch, mediaStore } = useContext(RtcContext)
  const rtmClientRef = useRef<RtmClient | null>(null)
  const rtmChannelRef = useRef<RtmChannel | null>(null)
  const rtmChannelJoinedRef = useRef<boolean>(false)
  const handleEndCallRef = useRef<
    ((isRemoteEnd?: boolean) => Promise<void>) | null
  >(null)
  // Tracks who actually ended the call so the doctor app can show the correct
  // "Call Ended" message. Values: 'doctor' | 'patient' | 'timeout'.
  const callEndedByRef = useRef<string | null>(null)
  const countdownTimerRef = useRef<number | undefined>(undefined)
  const localAudioTrackRef = useRef<any>(null) // Store reference to local audio track
  const isEndingCallRef = useRef<boolean>(false) // Guard to prevent multiple simultaneous end calls
  // Reconnection (§1): set true only when the call ends INTENTIONALLY (explicit End button, an
  // `end-call` RTM message from the other side, or the slot-end timer). A bare RTM `MemberLeft`
  // without this flag is treated as a temporary disconnect (keep the call open for rejoin).
  const intentionalEndRef = useRef<boolean>(false)
  // AI call summary recorder (doctor client only). Additive — never affects the call flow.
  const aiRecorderRef = useRef<AiCallRecorder | null>(null)
  const mediaStoreRef = useRef<any>(null)

  // Patch RTCPeerConnection.getStats to fix the error
  // This polyfill handles Agora track objects by extracting the real MediaStreamTrack
  // Option 1: Call getStats() without arguments (always safe)
  // Option 2: Extract real MediaStreamTrack from Agora tracks using .getMediaStreamTrack()
  useEffect(() => {
    // Store original getStats method
    const originalGetStats = RTCPeerConnection.prototype.getStats

    // Patch getStats to properly extract MediaStreamTrack from Agora tracks
    // Per Agora documentation: Use track.getMediaStreamTrack() to get the real MediaStreamTrack
    RTCPeerConnection.prototype.getStats = function (
      selector?: MediaStreamTrack | null | any
    ) {
      // If no selector, call getStats() without arguments (always safe)
      if (selector === null || selector === undefined) {
        return originalGetStats.call(this)
      }

      // If selector is already a MediaStreamTrack, use it directly
      if (selector instanceof MediaStreamTrack) {
        // Validate the track is live before using it
        if (selector.readyState === 'live') {
          return originalGetStats.call(this, selector)
        } else {
          // Track is not live - fallback to getStats() without arguments
          return originalGetStats.call(this)
        }
      }

      // If selector is an Agora track object, extract the real MediaStreamTrack
      if (selector && typeof selector === 'object') {
        try {
          // Primary method: Use getMediaStreamTrack() as per Agora documentation
          // Example: remoteUser.videoTrack.getMediaStreamTrack()
          if (typeof selector.getMediaStreamTrack === 'function') {
            const realTrack = selector.getMediaStreamTrack()

            // Validate the extracted track
            if (realTrack instanceof MediaStreamTrack) {
              // Validate the track is live before using it
              if (realTrack.readyState === 'live') {
                return originalGetStats.call(this, realTrack)
              } else {
                // Track is not live - fallback to getStats() without arguments
                return originalGetStats.call(this)
              }
            }
          }

          // Fallback: If getMediaStreamTrack() doesn't work, try other methods
          // (for compatibility with different Agora SDK versions or track types)
          if (selector._mediaStreamTrack instanceof MediaStreamTrack) {
            const realTrack = selector._mediaStreamTrack
            if (realTrack.readyState === 'live') {
              return originalGetStats.call(this, realTrack)
            }
          }

          // If we can't extract a valid MediaStreamTrack, fallback to getStats() without arguments
          return originalGetStats.call(this)
        } catch (e) {
          // Any error during track extraction - fallback to getStats() without arguments
          return originalGetStats.call(this)
        }
      }

      // Invalid selector type - fallback to getStats() without arguments
      return originalGetStats.call(this)
    }

    // Add global error handler for unhandled promise rejections as backup
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason
      const errorMessage = error?.message || ''
      const errorString = String(error || '')

      if (
        error &&
        (errorMessage.includes('getStats') ||
          errorMessage.includes('MediaStreamTrack') ||
          errorMessage.includes('RTCPeerConnection') ||
          errorString.includes('getStats'))
      ) {
        console.warn(
          'Caught getStats error (backup handler). This is non-critical:',
          error
        )
        event.preventDefault()
        event.stopPropagation()
      }
    }

    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    return () => {
      // Restore original getStats on cleanup
      RTCPeerConnection.prototype.getStats = originalGetStats
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [])

  // Function to validate appointment status before joining
  const validateAppointmentStatus = async (
    appointmentId: number | undefined,
    authToken: string | undefined,
    role: UserRole,
    refreshToken?: string | undefined
  ): Promise<{
    isValid: boolean
    error?: string
    /** Appointment detail, when it could be read (used for participant names). */
    appointment?: any
    /** The access token that worked — a refreshed one if the link's token had expired. */
    authToken?: string
  }> => {
    if (!appointmentId || !authToken) {
      // If we don't have appointment_id or auth_token, we can't validate
      // Allow joining to proceed (backend will handle validation)
      return { isValid: true }
    }

    try {
      // Determine the API endpoint based on role
      const baseUrl =
        process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'
      const endpoint =
        role === 'doctor'
          ? `${baseUrl}/consultation/doctor/appointments/${appointmentId}`
          : `${baseUrl}/patient/appointments/${appointmentId}`

      let currentToken = authToken

      // First attempt to validate with current token
      let response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${currentToken}`,
          'Content-Type': 'application/json'
        }
      })

      // If token expired (401) and we have a refresh token, try to refresh
      if (response.status === 401 && refreshToken) {
        console.log('🔄 Access token expired, attempting to refresh...')
        const refreshResult = await refreshAccessToken(refreshToken, role)

        if (refreshResult && refreshResult.accessToken) {
          console.log('✅ Token refreshed successfully, retrying validation')
          currentToken = refreshResult.accessToken

          // Retry validation with new token
          response = await fetch(endpoint, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${currentToken}`,
              'Content-Type': 'application/json'
            }
          })
        } else {
          console.warn(
            '⚠️ Could not refresh token, proceeding with join (backend will handle validation)'
          )
          // If refresh fails, allow joining - backend will handle validation
          return { isValid: true }
        }
      }

      if (!response.ok) {
        // If we still can't fetch appointment after refresh attempt, allow joining
        // Backend will handle validation
        console.warn(
          'Could not validate appointment status, proceeding with join:',
          response.status
        )
        return { isValid: true }
      }

      const data = await response.json()

      if (data && data.data) {
        const appointment = data.data
        const status = appointment.status

        // Check if appointment is completed
        if (status === 'completed') {
          return {
            isValid: false,
            error:
              'This call has already ended. You cannot rejoin a completed appointment.'
          }
        }

        // Check if appointment has session_ended_at (indicates call was ended)
        if (appointment.session_ended_at) {
          return {
            isValid: false,
            error:
              'This call has already ended. You cannot rejoin a completed appointment.'
          }
        }

        return { isValid: true, appointment, authToken: currentToken }
      }

      return { isValid: true, authToken: currentToken }
    } catch (error) {
      // If validation fails (network error, etc.), allow joining to proceed
      // Backend will handle validation
      console.warn(
        'Error validating appointment status, proceeding with join:',
        error
      )
      return { isValid: true }
    }
  }

  useEffect(() => {
    let countdownTimer: number | undefined
    const {
      joinData,
      username: initialName,
      role: parsedRole,
      error: parseError
    } = parseQueryBootstrap()

    setRole(parsedRole)
    if (initialName) {
      setUsername(initialName)
    } else {
      setUsername(parsedRole === 'patient' ? 'Patient' : 'Doctor')
    }

    if (parseError) {
      setError(parseError)
    }

    if (joinData) {
      // Store tokens from URL in localStorage/sessionStorage on initial load
      // This ensures tokens persist even if joinDetails is lost on page refresh
      if (typeof window !== 'undefined') {
        try {
          if (joinData.auth_token) {
            localStorage.setItem('access_token', joinData.auth_token)
            localStorage.setItem('auth_token', joinData.auth_token)
            sessionStorage.setItem('access_token', joinData.auth_token)
            sessionStorage.setItem('auth_token', joinData.auth_token)
          }
          if (joinData.refresh_token) {
            localStorage.setItem('refresh_token', joinData.refresh_token)
            sessionStorage.setItem('refresh_token', joinData.refresh_token)
          }
          console.log('✅ Initial tokens stored in localStorage/sessionStorage')
        } catch (error) {
          console.warn('⚠️ Failed to store initial tokens:', error)
        }
      }

      // Validate appointment status before joining
      validateAppointmentStatus(
        joinData.appointment_id,
        joinData.auth_token,
        parsedRole,
        joinData.refresh_token
      ).then((validation) => {
        if (!validation.isValid) {
          // Appointment is completed, show error and prevent joining
          setError(validation.error || 'This call has already ended.')
          setJoinDetails(null)
          setVideocall(false)
          return
        }

        // If the link's access token had expired and validation refreshed it, carry the fresh
        // one forward — the chat and every API call read it from here.
        if (
          validation.authToken &&
          validation.authToken !== joinData.auth_token
        ) {
          joinData.auth_token = validation.authToken
        }

        // Real names for the video tiles: from the join link, else from the appointment detail.
        const namesFromAppointment = extractParticipantNames(
          validation.appointment
        )
        setParticipantNames({
          doctor: joinData.doctor_name || namesFromAppointment.doctor,
          patient: joinData.patient_name || namesFromAppointment.patient
        })

        // Appointment is valid, proceed with joining
        setJoinDetails(joinData)
        // Both doctor and patient should be 'host' for video consultations
        // This allows both to publish video/audio and properly detect when the other leaves
        setHost(true)
        setVideocall(true)

        // Note: handleEndCallRef will be set when handleEndCall function is first called
        // The timer expiration handler will check if the ref is available before calling it

        // Initialize RTM client and channel for end call notifications
        if (
          joinData.agora_app_id &&
          joinData.rtm_token &&
          joinData.channel_name
        ) {
          try {
            console.log('Initializing RTM client with:', {
              appId: joinData.agora_app_id,
              uid: joinData.uid.toString(),
              channelName: joinData.channel_name,
              hasToken: !!joinData.rtm_token,
              tokenLength: joinData.rtm_token?.length
            })

            const client = AgoraRTM.createInstance(joinData.agora_app_id)
            rtmClientRef.current = client as RtmClient

            let connectionState: string = 'DISCONNECTED'

            client.on(
              'ConnectionStateChanged',
              (newState: any, reason: any) => {
                connectionState = newState
                console.log('RTM connection state changed:', {
                  newState,
                  reason,
                  state: connectionState
                })

                // If connection fails, log detailed error
                if (newState === 'DISCONNECTED' && reason) {
                  console.error('RTM disconnected. Reason:', reason)
                }
              }
            )

            // Wait for client to be ready before logging in
            const initializeRTM = async () => {
              try {
                // Validate UID before using it
                const rtmUid = joinData.uid.toString()

                // Check if UID is valid (should be numeric string, no special characters)
                if (!/^\d+$/.test(rtmUid)) {
                  console.error(
                    'Invalid RTM UID format. Expected numeric string, got:',
                    rtmUid,
                    'Original uid:',
                    joinData.uid
                  )
                  rtmChannelJoinedRef.current = false
                  return // Don't attempt RTM login with invalid UID
                }

                // Validate RTM token exists and is not empty
                if (!joinData.rtm_token || joinData.rtm_token.trim() === '') {
                  console.error('RTM token is missing or empty')
                  rtmChannelJoinedRef.current = false
                  return
                }

                // Login to RTM
                console.log('Logging in to RTM with validated credentials:', {
                  uid: rtmUid,
                  uidType: typeof rtmUid,
                  tokenLength: joinData.rtm_token.length,
                  channelName: joinData.channel_name
                })

                await client.login({
                  uid: rtmUid,
                  token: joinData.rtm_token
                })
                console.log('RTM client logged in successfully')

                // Wait a bit to ensure connection is stable
                await new Promise((resolve) => setTimeout(resolve, 500))

                // Check connection state before creating channel
                if (connectionState !== 'CONNECTED') {
                  console.warn(
                    'RTM connection state is not CONNECTED before creating channel:',
                    connectionState
                  )
                  // Still try to proceed, but log warning
                }

                const channel = client.createChannel(joinData.channel_name)
                rtmChannelRef.current = channel as RtmChannel

                // Set up message listener BEFORE joining the channel
                channel.on(
                  'ChannelMessage',
                  (message: any, memberId: string) => {
                    console.log(
                      'Received RTM channel message:',
                      message,
                      'from:',
                      memberId,
                      'message type:',
                      typeof message
                    )
                    try {
                      // Handle different message formats from Agora RTM SDK
                      let messageText = ''
                      if (typeof message === 'string') {
                        messageText = message
                      } else if (message?.text) {
                        messageText = message.text
                      } else if (message?.message?.text) {
                        messageText = message.message.text
                      } else if (message?.getText) {
                        messageText = message.getText()
                      }

                      console.log('Extracted message text:', messageText)

                      if (messageText) {
                        const data = JSON.parse(messageText)
                        console.log('Parsed RTM message data:', data)
                        if (data.type === 'end-call') {
                          console.log(
                            'Received end call message from',
                            memberId,
                            'role:',
                            data.role
                          )
                          // This is an INTENTIONAL end by the other participant — allow the
                          // subsequent MemberLeft to actually end/complete the call.
                          intentionalEndRef.current = true
                          setRemoteDisconnected(false)
                          // Record who ended the call (the sender's role) so the
                          // doctor app can show the correct "Call Ended" message.
                          callEndedByRef.current =
                            data.role === 'doctor' ? 'doctor' : 'patient'
                          // Trigger end call for this participant
                          // Use the ref to call handleEndCall
                          if (handleEndCallRef.current) {
                            console.log('Calling handleEndCall via ref')
                            handleEndCallRef.current(true)
                          } else {
                            console.log(
                              'handleEndCall ref not available, using fallback'
                            )
                            // Fallback: directly end the call
                            // Clear the countdown timer
                            if (countdownTimerRef.current) {
                              window.clearInterval(countdownTimerRef.current)
                              countdownTimerRef.current = undefined
                            }
                            setTimeRemaining(null)
                            exitFullscreen()
                            setVideocall(false)
                            setJoinDetails(null)
                            setError(
                              'The meeting has ended. The other participant has left the call.'
                            )
                          }
                        }
                      } else {
                        console.warn(
                          'Could not extract message text from RTM message'
                        )
                      }
                    } catch (error) {
                      console.error(
                        'Error parsing RTM message:',
                        error,
                        'Raw message:',
                        message
                      )
                    }
                  }
                )

                // Member left the RTM channel. This fires both for an intentional end and for a
                // temporary drop (network loss / app crash / backgrounding). We only END the call
                // when the leave was INTENTIONAL (an `end-call` message was received, or this
                // client already ended). Otherwise we treat it as a temporary disconnect and keep
                // the call open so the participant can rejoin until the slot ends (§1 reconnection).
                channel.on('MemberLeft', (memberId: string) => {
                  console.log('RTM member left:', memberId, {
                    intentionalEnd: intentionalEndRef.current
                  })

                  if (intentionalEndRef.current) {
                    if (!callEndedByRef.current) {
                      callEndedByRef.current =
                        role === 'doctor' ? 'patient' : 'doctor'
                    }
                    if (handleEndCallRef.current) {
                      handleEndCallRef.current(true)
                    }
                    return
                  }

                  // Temporary disconnect: do NOT end/complete. Surface a non-terminal notice; the
                  // other side can rejoin until the slot ends (backend keeps the appointment
                  // ONGOING and auto-completes only at slot end).
                  console.log(
                    'Member left without an intentional end — treating as a temporary disconnect; keeping the call open for rejoin.'
                  )
                  setRemoteDisconnected(true)
                })

                // The other participant rejoined — clear the temporary-disconnect notice.
                channel.on('MemberJoined', (memberId: string) => {
                  console.log('RTM member joined/rejoined:', memberId)
                  setRemoteDisconnected(false)
                })

                // Join channel with retry logic
                const joinChannelWithRetry = async (
                  retries = 3,
                  delay = 1000
                ) => {
                  for (let i = 0; i < retries; i++) {
                    try {
                      console.log(
                        `Attempting to join RTM channel (attempt ${
                          i + 1
                        }/${retries})...`
                      )
                      await channel.join()
                      console.log(
                        'RTM channel joined successfully, ready to send/receive messages'
                      )
                      rtmChannelJoinedRef.current = true
                      return
                    } catch (error: any) {
                      console.error(
                        `Error joining RTM channel (attempt ${
                          i + 1
                        }/${retries}):`,
                        error
                      )

                      // Log detailed error information
                      if (error?.code) {
                        console.error('RTM error code:', error.code)
                      }
                      if (error?.reason) {
                        console.error('RTM error reason:', error.reason)
                      }
                      if (error?.message) {
                        console.error('RTM error message:', error.message)
                      }

                      // If it's the last retry, give up
                      if (i === retries - 1) {
                        console.error(
                          'Failed to join RTM channel after all retries. RTM features will not be available.'
                        )
                        rtmChannelJoinedRef.current = false
                        // Don't throw - RTM is not critical for the call to work
                        return
                      }

                      // Wait before retrying
                      await new Promise((resolve) => setTimeout(resolve, delay))
                      delay *= 2 // Exponential backoff
                    }
                  }
                }

                await joinChannelWithRetry()
              } catch (error: any) {
                console.error('Error in RTM initialization:', error)

                // Log detailed error information
                if (error?.code) {
                  console.error('RTM error code:', error.code)

                  // Provide user-friendly error messages for common error codes
                  const rejectionText = String(error?.message || '')
                  if (rejectionText.includes('2010026')) {
                    // Agora's documented meaning of server code 2010026: the Signaling (RTM)
                    // service is not enabled for this Agora project. It is a project setting,
                    // not a token problem — enable Signaling (select a data center) for this
                    // App ID in Agora Console. Video and chat keep working without it.
                    console.warn(
                      'RTM login rejected (2010026): Signaling is not enabled for this Agora project. ' +
                        'Enable it in Agora Console for this App ID. The video call continues; ' +
                        'end-call sync falls back to the RTC user-left event.'
                    )
                  } else if (error.code === 2) {
                    console.error(
                      'RTM Login Failed: the RTM token may be invalid or expired, or the UID does not match the token.'
                    )
                  }
                }
                if (error?.reason) {
                  console.error('RTM error reason:', error.reason)
                }
                if (error?.message) {
                  console.error('RTM error message:', error.message)
                }

                rtmChannelJoinedRef.current = false

                // Note: RTM failure is not critical - the video call will still work
                // The end-call synchronization will fall back to the user-left callback
                console.warn(
                  'RTM initialization failed. Video call will continue to work,',
                  'but real-time end-call synchronization may not be available.',
                  'The user-left callback will handle disconnection as a fallback.'
                )
              }
            }

            // Start RTM initialization
            initializeRTM().catch((error) => {
              console.error('Fatal error initializing RTM:', error)
              rtmChannelJoinedRef.current = false
            })
          } catch (error) {
            console.error('Error creating RTM client:', error)
            rtmChannelJoinedRef.current = false
          }
        } else {
          console.warn('RTM not initialized - missing required data:', {
            hasAppId: !!joinData.agora_app_id,
            hasRtmToken: !!joinData.rtm_token,
            hasChannelName: !!joinData.channel_name
          })
        }

        const expiry = new Date(joinData.expires_at).getTime()
        // 15 minutes, or 40 for an extended-slot doctor (from the join link).
        const slotDurationMs = consultationDurationMs(joinData)
        const tokenPreRefreshedRef = { done: false }
        const updateRemaining = async () => {
          const diffMs = expiry - Date.now()
          if (diffMs <= 0) {
            // Clear the countdown timer
            if (countdownTimerRef.current) {
              window.clearInterval(countdownTimerRef.current)
              countdownTimerRef.current = undefined
            }
            setTimeRemaining('00:00')

            // The call ended because the session time is over.
            callEndedByRef.current = 'timeout'

            // Call the end call API to save details in database when timer expires
            if (handleEndCallRef.current) {
              console.log(
                'Timer expired, calling end call API to save session details'
              )
              try {
                await handleEndCallRef.current(true) // Pass true to indicate it's a remote/automatic end
              } catch (error) {
                console.error(
                  'Error calling end call API on timer expiration:',
                  error
                )
                // Continue with local cleanup even if API fails
              }
            } else {
              console.warn(
                'handleEndCallRef not available when timer expired, ending call locally only'
              )
              exitFullscreen()
              setVideocall(false)
              setJoinDetails(null)
            }

            // After the timer ends, redirect the DOCTOR to the consultation/appointment
            // details page — the same destination as when the doctor manually ends the
            // call. Guaranteed here in case the end-call redirect did not run.
            try {
              const params = new URL(window.location.href).searchParams
              const roleParam = (params.get('role') || '').toLowerCase()
              const apptId =
                params.get('appointment_id') ||
                (joinData.appointment_id != null
                  ? String(joinData.appointment_id)
                  : null)
              const patientIdParam = params.get('patient_id')
              if (roleParam === 'doctor' && apptId && patientIdParam) {
                const doctorAppUrl =
                  process.env.REACT_APP_DOCTOR_APP_URL ||
                  'http://localhost:3000'
                const endedByParam = callEndedByRef.current || 'timeout'
                window.location.href = `${doctorAppUrl}/online?appointment_id=${apptId}&patient_id=${patientIdParam}&call_ended=true&ended_by=${endedByParam}`
              } else if (roleParam !== 'doctor') {
                setError(
                  'The meeting has ended because the session time is over.'
                )
              }
            } catch (redirectErr) {
              console.warn(
                'Failed to redirect after timer expiry:',
                redirectErr
              )
            }
            return false
          }
          const totalSeconds = Math.floor(diffMs / 1000)
          // The join window opens before the scheduled time, so `diffMs` can be longer than the
          // slot (e.g. 23:00 when joining 8 minutes early for a 15-minute slot). Show the
          // consultation's own time instead: it holds at the slot length (15:00, or 40:00) until
          // the scheduled start, then counts down. The call still ends at the slot end.
          const untilStartMs = diffMs - slotDurationMs
          setTimeRemaining(formatCountdown(Math.min(diffMs, slotDurationMs)))
          setTimeUntilStart(
            untilStartMs >= 1000 ? formatCountdown(untilStartMs) : null
          )
          // Pre-refresh token when 3 minutes remain to ensure end-call API works
          if (
            totalSeconds <= 180 &&
            !tokenPreRefreshedRef.done &&
            joinData.session_id
          ) {
            tokenPreRefreshedRef.done = true
            console.log('⏰ 3 minutes remaining - pre-refreshing auth token...')
            try {
              const result = await fetchTokensBySessionId(joinData.session_id)
              if (result) {
                // Store fresh tokens so handleEndCall can use them
                localStorage.setItem('access_token', result.accessToken)
                localStorage.setItem('refresh_token', result.refreshToken)
                sessionStorage.setItem('access_token', result.accessToken)
                sessionStorage.setItem('refresh_token', result.refreshToken)
                if (joinDetails) {
                  setJoinDetails({
                    ...joinDetails,
                    auth_token: result.accessToken,
                    refresh_token: result.refreshToken
                  })
                }
                console.log('✅ Token pre-refreshed successfully')
              }
            } catch (e) {
              console.warn('⚠️ Token pre-refresh failed:', e)
            }
          }
          return true
        }
        updateRemaining()
        countdownTimer = window.setInterval(() => {
          if (!updateRemaining() && countdownTimer) {
            window.clearInterval(countdownTimer)
          }
        }, 1000)
        countdownTimerRef.current = countdownTimer
      })
    }

    return () => {
      if (countdownTimer) {
        window.clearInterval(countdownTimer)
      }
      if (countdownTimerRef.current) {
        window.clearInterval(countdownTimerRef.current)
        countdownTimerRef.current = undefined
      }
      // Cleanup RTM on unmount
      if (rtmChannelRef.current) {
        rtmChannelRef.current.leave().catch(console.error)
        rtmChannelRef.current = null
        rtmChannelJoinedRef.current = false
      }
      if (rtmClientRef.current) {
        rtmClientRef.current.logout().catch(console.error)
        rtmClientRef.current = null
      }
    }
  }, [])

  // Set handleEndCallRef when videocall starts so it's available for timer expiration
  // Note: handleEndCall is defined later in the component, so we use a ref pattern
  // The ref will be set when handleEndCall is first called, or we can set it here
  // by accessing the function through closure
  useEffect(() => {
    if (videocall) {
      // Ensure handleEndCallRef is set immediately when videocall starts
      // so it's available for timer expiration and RTM handlers
      handleEndCallRef.current = handleEndCall
    }
  }, [videocall])

  /**
   * Whether the server considers this consultation finished (someone pressed End, or the
   * slot-end job completed it). Same rule as validateAppointmentStatus at join time.
   * 'unknown' when it cannot be checked (no appointment id or token, network error); callers
   * must NOT treat that as ended.
   */
  const fetchAppointmentEndState = async (): Promise<
    'ended' | 'active' | 'unknown'
  > => {
    const details = joinDetailsRef.current
    const fromChannel = details?.channel_name?.match(/appointment_(\d+)/)?.[1]
    const appointmentId =
      details?.appointment_id || (fromChannel ? Number(fromChannel) : null)
    if (!appointmentId) return 'unknown'

    try {
      const token = await getFreshAuthToken()
      if (!token) return 'unknown'
      const baseUrl =
        process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'
      const endpoint =
        role === 'doctor'
          ? `${baseUrl}/consultation/doctor/appointments/${appointmentId}`
          : `${baseUrl}/patient/appointments/${appointmentId}`
      const response = await fetch(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      if (!response.ok) return 'unknown'
      const appointment = (await response.json())?.data
      if (!appointment) return 'unknown'
      return appointment.status === 'completed' || appointment.session_ended_at
        ? 'ended'
        : 'active'
    } catch (error) {
      console.warn('[Reconnect] Could not check the appointment status:', error)
      return 'unknown'
    }
  }

  // The other participant's media session went away (RTC `user-left` or RTM `MemberLeft`).
  //
  // A page refresh, a network drop and an intentional End all look identical from here. The
  // old handler ended (and COMPLETED) the consultation on any of them, so refreshing the page
  // killed the call for both sides even with time left. Now the call stays open with a notice,
  // and is ended only once the server says the consultation is over. The participant who
  // pressed End completes it on the server BEFORE leaving the channel, so the first check
  // normally catches an intentional end straight away; the polling covers ends we would
  // otherwise miss (e.g. the RTM end-call message is unavailable on this project).
  useEffect(() => {
    if (!videocall || !remoteDisconnected) return
    let cancelled = false
    let timer: number | undefined

    const check = async () => {
      const state = await fetchAppointmentEndState()
      if (cancelled) return
      if (state === 'ended') {
        console.log(
          '[Reconnect] The consultation has ended on the server; ending the call here too.'
        )
        intentionalEndRef.current = true
        if (!callEndedByRef.current) {
          callEndedByRef.current = role === 'doctor' ? 'patient' : 'doctor'
        }
        handleEndCall(true)
        return
      }
      console.log(
        `[Reconnect] Other participant disconnected; consultation is ${state}. Waiting for them to rejoin.`
      )
      timer = window.setTimeout(check, REMOTE_END_POLL_MS)
    }

    check()
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [videocall, remoteDisconnected])

  useEffect(() => {
    if (isFullscreen) {
      if (previousBodyOverflow.current === null) {
        previousBodyOverflow.current = document.body.style.overflow
      }
      document.body.style.overflow = 'hidden'
    } else if (previousBodyOverflow.current !== null) {
      document.body.style.overflow = previousBodyOverflow.current
      previousBodyOverflow.current = null
    }

    return () => {
      if (previousBodyOverflow.current !== null) {
        document.body.style.overflow = previousBodyOverflow.current
        previousBodyOverflow.current = null
      }
    }
  }, [isFullscreen])

  useEffect(() => {
    return () => {
      if (previousBodyOverflow.current !== null) {
        document.body.style.overflow = previousBodyOverflow.current
        previousBodyOverflow.current = null
      }
    }
  }, [])

  // Browser Fullscreen API helpers

  const exitDomFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen()
      } else if ((document as any).webkitFullscreenElement) {
        await (document as any).webkitExitFullscreen?.()
      } else if ((document as any).msFullscreenElement) {
        await (document as any).msExitFullscreen?.()
      }
    } catch {
      // ignore
    }
  }

  const enterDomFullscreen = async () => {
    try {
      const node = stageWrapperRef.current
      if (!node) return
      if (node.requestFullscreen) {
        await node.requestFullscreen()
      } else if ((node as any).webkitRequestFullscreen) {
        await (node as any).webkitRequestFullscreen()
      } else if ((node as any).msRequestFullscreen) {
        await (node as any).msRequestFullscreen()
      }
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    const onFsChange = () => {
      const active =
        !!document.fullscreenElement ||
        !!(document as any).webkitFullscreenElement ||
        !!(document as any).msFullscreenElement
      setIsFullscreen(active)
    }
    document.addEventListener('fullscreenchange', onFsChange)
    ;(document as any).addEventListener?.('webkitfullscreenchange', onFsChange)
    ;(document as any).addEventListener?.('MSFullscreenChange', onFsChange)
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange)
      ;(document as any).removeEventListener?.(
        'webkitfullscreenchange',
        onFsChange
      )
      ;(document as any).removeEventListener?.('MSFullscreenChange', onFsChange)
    }
  }, [])

  // Store local audio track reference
  useEffect(() => {
    if (!videocall || !mediaStore || !joinDetails) return

    // Try to find local audio track from multiple sources
    let localAudioTrack: any = null
    const localUid = String(joinDetails.uid)

    // Method 1: mediaStore.localMedia.audioTrack
    if (mediaStore?.localMedia?.audioTrack) {
      localAudioTrack = mediaStore.localMedia.audioTrack
    }

    // Method 2: mediaStore[localUid].audioTrack
    if (!localAudioTrack) {
      const localUserMedia =
        (mediaStore as any)?.[localUid] ||
        (mediaStore as any)?.[Number(localUid)]
      if (localUserMedia?.audioTrack) {
        localAudioTrack = localUserMedia.audioTrack
      }
    }

    // Method 3: Check max view data
    if (!localAudioTrack && mediaStore?.max) {
      const maxViewData = Array.isArray(mediaStore.max)
        ? mediaStore.max[0]
        : mediaStore.max
      if (maxViewData && String((maxViewData as any)?.uid) === localUid) {
        localAudioTrack = (maxViewData as any)?.audioTrack
      }
    }

    // Store the track reference
    if (localAudioTrack) {
      localAudioTrackRef.current = localAudioTrack
    }
  }, [videocall, mediaStore, joinDetails])

  // Keep the latest mediaStore accessible to the AI recorder's track getters.
  useEffect(() => {
    mediaStoreRef.current = mediaStore
  }, [mediaStore])

  // Start the AI call-summary recorder — DOCTOR client only, and only when we have the
  // appointment id + auth token. Fully additive: any failure is swallowed so the call is
  // never affected. The recorder captures spoken audio for the doctor-verified AI summary.
  useEffect(() => {
    if (role !== 'doctor' || !videocall || !joinDetails) return
    const appointmentId = joinDetails.appointment_id
    const authToken =
      joinDetails.auth_token ||
      (typeof window !== 'undefined'
        ? localStorage.getItem('access_token') ||
          localStorage.getItem('auth_token') ||
          ''
        : '')
    if (!appointmentId || !authToken) return
    if (aiRecorderRef.current) return

    const extractTrack = (t: any): MediaStreamTrack | null => {
      if (!t) return null
      try {
        if (typeof t.getMediaStreamTrack === 'function') {
          const mst = t.getMediaStreamTrack()
          return mst instanceof MediaStreamTrack ? mst : null
        }
        if (t instanceof MediaStreamTrack) return t
        if (t._mediaStreamTrack instanceof MediaStreamTrack)
          return t._mediaStreamTrack
      } catch {
        return null
      }
      return null
    }

    const getDoctorTrack = (): MediaStreamTrack | null =>
      extractTrack(localAudioTrackRef.current)

    const getPatientTrack = (): MediaStreamTrack | null => {
      const localUid = String(joinDetails.uid)
      const store: any = mediaStoreRef.current || {}
      for (const key of Object.keys(store)) {
        if (key === localUid || key === 'localMedia' || key === 'max') continue
        const at = store[key]?.audioTrack
        const mst = extractTrack(at)
        if (mst && mst.readyState === 'live') return mst
      }
      return null
    }

    try {
      const apiBaseUrl =
        process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'
      const recorder = new AiCallRecorder({
        apiBaseUrl,
        appointmentId,
        authToken,
        getDoctorTrack,
        getPatientTrack
      })
      aiRecorderRef.current = recorder
      recorder.start()
      console.log('[AI recorder] started for appointment', appointmentId)
    } catch (e) {
      console.warn('[AI recorder] failed to start:', e)
    }
  }, [role, videocall, joinDetails])

  // Removed custom fullscreen toggle button to avoid duplicates with UIKit

  const exitFullscreen = async () => {
    await exitDomFullscreen()
    setIsFullscreen(false)
  }

  // Function to fetch fresh tokens using session_id (PRIMARY APPROACH)
  // This fetches tokens directly from the database using session_id
  // Returns both access token and refresh token
  const fetchTokensBySessionId = async (
    sessionId: string
  ): Promise<{ accessToken: string; refreshToken: string } | null> => {
    try {
      const apiBaseUrl =
        process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'
      const endpoint = `${apiBaseUrl}/consultation/session/${encodeURIComponent(
        sessionId
      )}/tokens`

      console.log('🔄 Fetching fresh tokens using session_id:', {
        sessionIdPreview: sessionId.substring(0, 20) + '...',
        endpoint
      })

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const errorText = await response.text()
        let errorData: any = null
        try {
          errorData = JSON.parse(errorText)
        } catch (e) {
          // Error response is not JSON
        }

        console.error('❌ Failed to fetch tokens by session_id:', {
          status: response.status,
          statusText: response.statusText,
          errorText,
          errorData
        })
        return null
      }

      const data = await response.json()
      if (
        data?.status &&
        data?.data?.access_token &&
        data?.data?.refresh_token
      ) {
        console.log('✅ Fresh tokens fetched successfully using session_id')
        return {
          accessToken: data.data.access_token,
          refreshToken: data.data.refresh_token
        }
      } else {
        console.error(
          '❌ Invalid response format from session tokens endpoint:',
          data
        )
        return null
      }
    } catch (error) {
      console.error('❌ Error fetching tokens by session_id:', error)
      return null
    }
  }

  // Function to refresh the access token (FALLBACK APPROACH)
  // Returns both new access token and new refresh token (if provided)
  const refreshAccessToken = async (
    refreshToken: string,
    userRole: UserRole
  ): Promise<{ accessToken: string; refreshToken?: string } | null> => {
    try {
      const apiBaseUrl =
        process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'

      // Use different endpoints based on role
      // Doctor/Pharmacist use: /consultation/refresh-token
      // Patient uses: /auth/patient/refresh-token
      const refreshEndpoint =
        userRole === 'doctor'
          ? `${apiBaseUrl}/consultation/refresh-token`
          : `${apiBaseUrl}/auth/patient/refresh-token`

      // Validate refresh token before attempting refresh
      if (!refreshToken || refreshToken.trim() === '') {
        console.error('❌ Refresh token is empty or invalid')
        return null
      }

      console.log('🔄 Refreshing access token:', {
        endpoint: refreshEndpoint,
        role: userRole,
        hasRefreshToken: !!refreshToken,
        refreshTokenLength: refreshToken.length,
        refreshTokenPreview: refreshToken.substring(0, 20) + '...'
      })

      const response = await fetch(refreshEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          refresh_token: refreshToken
        })
      })

      if (!response.ok) {
        const errorText = await response.text()
        let errorData: any = null
        try {
          errorData = JSON.parse(errorText)
        } catch (e) {
          // Error response is not JSON
        }

        console.error('❌ Token refresh failed:', {
          status: response.status,
          statusText: response.statusText,
          errorText,
          errorData
        })

        // If refresh token is invalid/expired, return null so we can still try the API call
        // The API call might still work with the original token, or we'll handle 401 gracefully
        return null
      }

      const data = await response.json()
      if (data && data.data && data.data.access_token) {
        console.log('✅ Token refreshed successfully')

        const newAccessToken = data.data.access_token
        const newRefreshToken = data.data.refresh_token

        // 1. Update joinDetails with new tokens (for current session)
        if (joinDetails) {
          joinDetails.auth_token = newAccessToken
          // Note: joinDetails doesn't have access_token field, but we'll use auth_token
          // IMPORTANT: joinDetails.auth_token is updated with the latest token after refresh
          if (newRefreshToken) {
            joinDetails.refresh_token = newRefreshToken
          }
        }

        // 2. Store new tokens in localStorage (persists across page refreshes)
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem('access_token', newAccessToken)
            localStorage.setItem('auth_token', newAccessToken) // For backward compatibility
            if (newRefreshToken) {
              localStorage.setItem('refresh_token', newRefreshToken)
            }
            console.log('✅ New tokens stored in localStorage')
          } catch (error) {
            console.warn('⚠️ Failed to store tokens in localStorage:', error)
          }
        }

        // 3. Also store in sessionStorage as backup
        if (typeof window !== 'undefined') {
          try {
            sessionStorage.setItem('access_token', newAccessToken)
            sessionStorage.setItem('auth_token', newAccessToken) // For backward compatibility
            if (newRefreshToken) {
              sessionStorage.setItem('refresh_token', newRefreshToken)
            }
            console.log('✅ New tokens stored in sessionStorage')
          } catch (error) {
            console.warn('⚠️ Failed to store tokens in sessionStorage:', error)
          }
        }

        // 4. Update URL with new tokens (optional - for better UX, but don't replace history)
        if (typeof window !== 'undefined' && newRefreshToken) {
          try {
            const currentUrl = new URL(window.location.href)
            const urlParams = currentUrl.searchParams

            // Update tokens in URL params
            urlParams.set('access_token', newAccessToken)
            urlParams.set('auth_token', newAccessToken) // For backward compatibility
            urlParams.set('refresh_token', newRefreshToken)

            // Update URL without page reload (using replaceState to avoid adding to history)
            const newUrl = `${window.location.pathname}?${urlParams.toString()}`
            window.history.replaceState({}, '', newUrl)
            console.log('✅ URL updated with new tokens')
          } catch (error) {
            console.warn('⚠️ Failed to update URL with new tokens:', error)
            // Non-critical, continue anyway
          }
        }

        // Return both tokens
        return {
          accessToken: newAccessToken,
          refreshToken: newRefreshToken
        }
      } else {
        console.error('❌ Token refresh response missing access_token:', data)
        return null
      }
    } catch (error) {
      console.error('❌ Error refreshing token:', error)
      return null
    }
  }

  /**
   * A currently-valid access token for this participant, refreshed when the current one is
   * expired or expires within `minValiditySeconds`.
   *
   * Access tokens live 15 minutes while a consultation can run longer, and the token in the
   * join link may already be old when the page opens. The chat socket in particular needs a
   * valid token on every (re)connection, so it calls this rather than reusing the link's token.
   * Tokens that belong to a different user (another tab on the same origin writing to the shared
   * localStorage) are ignored.
   */
  const getFreshAuthToken = async (
    minValiditySeconds: number = 60
  ): Promise<string | null> => {
    const details = joinDetailsRef.current
    const belongsToMe = (token: string | null | undefined): token is string => {
      if (!token) return false
      const { payload } = decodeJWT(token)
      if (!payload) return false
      const tokenUserId =
        payload.userId ?? payload.user_id ?? payload.sub ?? payload.id
      return (
        !details?.uid ||
        tokenUserId == null ||
        Number(tokenUserId) === Number(details.uid)
      )
    }
    const isStillValid = (token: string) => {
      const { payload } = decodeJWT(token)
      if (!payload?.exp) return true
      return payload.exp * 1000 - Date.now() > minValiditySeconds * 1000
    }

    const candidates = [
      (() => {
        try {
          return sessionStorage.getItem('access_token')
        } catch {
          return null
        }
      })(),
      details?.auth_token,
      readStoredValue('access_token')
    ].filter(belongsToMe)
    const current = candidates[0] || null
    if (current && isStillValid(current)) return current

    if (tokenRefreshInFlightRef.current) return tokenRefreshInFlightRef.current

    const refresh = (async (): Promise<string | null> => {
      if (details?.session_id) {
        const result = await fetchTokensBySessionId(details.session_id)
        if (result?.accessToken && belongsToMe(result.accessToken)) {
          storeAuthTokens(result.accessToken, result.refreshToken)
          return result.accessToken
        }
      }
      const refreshToken =
        readStoredValue('refresh_token') || details?.refresh_token
      if (refreshToken) {
        // refreshAccessToken stores the new tokens itself.
        const result = await refreshAccessToken(refreshToken, role)
        if (result?.accessToken) return result.accessToken
      }
      console.warn(
        '⚠️ Could not refresh the access token; using the current one.'
      )
      return current
    })()

    tokenRefreshInFlightRef.current = refresh
    try {
      return await refresh
    } finally {
      tokenRefreshInFlightRef.current = null
    }
  }

  const handleEndCall = async (isRemoteEnd: boolean = false) => {
    // Prevent multiple simultaneous end call attempts
    if (isEndingCallRef.current) {
      console.log('Call is already ending, ignoring duplicate request')
      return
    }

    console.log('handleEndCall called:', {
      isRemoteEnd,
      role,
      hasJoinDetails: !!joinDetails
    })

    // Set guard to prevent duplicate calls
    isEndingCallRef.current = true
    // Reaching handleEndCall always means an intentional end (End button, received end-call
    // message, or slot-end timer) — never a bare disconnect. Mark it so any subsequent
    // MemberLeft completes the call rather than being treated as a temporary drop.
    intentionalEndRef.current = true

    // Record who ended the call if not already set by an RTM/member-left event.
    // A local end (isRemoteEnd === false) means this client's user ended it;
    // a remote end without an explicit sender means the other participant did.
    if (!callEndedByRef.current) {
      callEndedByRef.current = isRemoteEnd
        ? role === 'doctor'
          ? 'patient'
          : 'doctor'
        : role || 'doctor'
    }

    // Update ref so RTM handler can access it
    handleEndCallRef.current = handleEndCall

    // Declare variables at function scope so they're accessible in finally block
    let appointmentId: number | null = null
    let authToken: string | null = null
    let finalRole: UserRole = role

    try {
      // Send RTM message to notify other participants (only if not already triggered by remote)
      // IMPORTANT: Send message BEFORE leaving the channel
      if (!isRemoteEnd && rtmChannelRef.current && joinDetails?.channel_name) {
        try {
          // Check if channel is joined, if not wait a bit
          if (!rtmChannelJoinedRef.current) {
            console.log(
              'Waiting for RTM channel to join before sending end call message...'
            )
            // Wait up to 2 seconds for channel to join
            for (let i = 0; i < 20; i++) {
              await new Promise((resolve) => setTimeout(resolve, 100))
              if (rtmChannelJoinedRef.current) break
            }
          }

          if (
            rtmChannelJoinedRef.current &&
            rtmChannelRef.current &&
            rtmClientRef.current
          ) {
            const messageText = JSON.stringify({
              type: 'end-call',
              timestamp: Date.now(),
              role: role
            })
            console.log(
              'Sending RTM end call message:',
              messageText,
              'Channel joined:',
              rtmChannelJoinedRef.current
            )

            // Use Agora RTM SDK to send message
            // The channel.sendMessage method requires a message object created with createMessage
            const client = rtmClientRef.current
            if (client && typeof (client as any).createMessage === 'function') {
              // Create RtmTextMessage using client.createMessage
              const message = (client as any).createMessage({
                text: messageText,
                messageType: 'TEXT'
              })
              // Send message and wait for it to complete
              await (rtmChannelRef.current as any).sendMessage(message)
              console.log('Successfully sent end call message via RTM')
              // Wait a small delay to ensure message is delivered
              await new Promise((resolve) => setTimeout(resolve, 200))
            } else {
              // Fallback: try sending as RtmTextMessage object directly
              const textMessage = { text: messageText, messageType: 'TEXT' }
              await (rtmChannelRef.current as any).sendMessage(textMessage)
              console.log(
                'Successfully sent end call message via RTM (fallback)'
              )
              // Wait a small delay to ensure message is delivered
              await new Promise((resolve) => setTimeout(resolve, 200))
            }
          } else {
            console.warn(
              'RTM channel not ready, cannot send message. Channel joined:',
              rtmChannelJoinedRef.current,
              'Has channel:',
              !!rtmChannelRef.current,
              'Has client:',
              !!rtmClientRef.current
            )
          }
        } catch (rtmError) {
          console.error('Error sending RTM end call message:', rtmError)
          console.warn(
            'RTM channel message failed, but continuing with call end. The user-left callback will handle disconnection.'
          )
        }
      } else if (!isRemoteEnd) {
        console.warn('Cannot send RTM message - channel not ready:', {
          hasChannel: !!rtmChannelRef.current,
          isJoined: rtmChannelJoinedRef.current,
          hasChannelName: !!joinDetails?.channel_name
        })
      }

      // Extract appointment_id from multiple sources
      // appointmentId is already declared at function scope

      console.log('Extracting appointment ID and auth token:', {
        hasJoinDetails: !!joinDetails,
        channelName: joinDetails?.channel_name,
        appointmentIdInDetails: joinDetails?.appointment_id
      })

      // 1. First try from URL params directly (most reliable)
      if (typeof window !== 'undefined') {
        const currentUrl = new URL(window.location.href)
        const urlParams = currentUrl.searchParams
        const appointmentIdFromUrl = urlParams.get('appointment_id')
        if (appointmentIdFromUrl) {
          const parsed = parseInt(appointmentIdFromUrl, 10)
          if (!isNaN(parsed) && parsed > 0) {
            appointmentId = parsed
            console.log('Using appointment_id from URL params:', appointmentId)
          }
        }
      }

      // 2. Try from joinDetails.appointment_id
      if (!appointmentId && joinDetails?.appointment_id) {
        appointmentId = joinDetails.appointment_id
        console.log('Using appointment_id from joinDetails:', appointmentId)
      }

      // 3. Try to extract from channel_name (format: appointment_{id})
      if (!appointmentId && joinDetails?.channel_name) {
        const match = joinDetails.channel_name.match(/appointment_(\d+)/)
        if (match && match[1]) {
          appointmentId = parseInt(match[1], 10)
          console.log(
            'Extracted appointment ID from channel_name:',
            appointmentId
          )
        } else {
          console.warn(
            'Could not extract appointment ID from channel_name:',
            joinDetails.channel_name
          )
        }
      }

      // Get access token from multiple sources
      // PRIORITY ORDER (most recent first):
      // 1. joinDetails (updated in-memory after refresh)
      // 2. localStorage (persists across page refreshes, updated after refresh)
      // 3. sessionStorage (backup, updated after refresh)
      // 4. URL params (may contain old tokens, use as last resort)
      // authToken is already declared at function scope

      // 1. First try from joinDetails (this gets updated when tokens are refreshed, so it has the latest token)
      if (joinDetails?.auth_token) {
        authToken = joinDetails.auth_token
        console.log('✅ Using auth_token from joinDetails (latest token)')
      }

      // 2. Try from localStorage (persists across page refreshes, updated after refresh)
      if (!authToken && typeof window !== 'undefined') {
        authToken =
          localStorage.getItem('access_token') ||
          localStorage.getItem('accessToken') ||
          localStorage.getItem('auth_token') ||
          localStorage.getItem('token')
        if (authToken) {
          console.log(
            '✅ Using access_token from localStorage (persisted token)'
          )
        }
      }

      // 3. Try from sessionStorage as backup (updated after refresh)
      if (!authToken && typeof window !== 'undefined') {
        authToken =
          sessionStorage.getItem('access_token') ||
          sessionStorage.getItem('accessToken') ||
          sessionStorage.getItem('auth_token') ||
          sessionStorage.getItem('token')
        if (authToken) {
          console.log(
            '✅ Using access_token from sessionStorage (backup token)'
          )
        }
      }

      // 4. Fallback to URL params (may contain old tokens, use as last resort)
      if (!authToken && typeof window !== 'undefined') {
        const currentUrl = new URL(window.location.href)
        const urlParams = currentUrl.searchParams
        authToken = urlParams.get('access_token') || urlParams.get('auth_token')
        if (authToken) {
          const source = urlParams.get('access_token')
            ? 'access_token'
            : 'auth_token'
          console.log(
            `⚠️ Using ${source} from URL params (fallback - may be old)`
          )
        }
      }

      // Get refresh token from multiple sources
      // PRIORITY ORDER (most recent first):
      // 1. joinDetails (updated in-memory after refresh)
      // 2. localStorage (persists across page refreshes, updated after refresh)
      // 3. sessionStorage (backup, updated after refresh)
      // 4. URL params (may contain old/invalid tokens, use as last resort)
      let refreshToken: string | null = null

      // 1. First try from joinDetails (this gets updated when tokens are refreshed, so it has the latest token)
      if (joinDetails?.refresh_token) {
        refreshToken = joinDetails.refresh_token
        console.log('✅ Using refresh_token from joinDetails (latest token)')
      }

      // 2. Try from localStorage (persists across page refreshes, updated after refresh)
      if (!refreshToken && typeof window !== 'undefined') {
        refreshToken = localStorage.getItem('refresh_token')
        if (refreshToken) {
          console.log(
            '✅ Using refresh_token from localStorage (persisted token)'
          )
        }
      }

      // 3. Try from sessionStorage as backup (updated after refresh)
      if (!refreshToken && typeof window !== 'undefined') {
        refreshToken = sessionStorage.getItem('refresh_token')
        if (refreshToken) {
          console.log(
            '✅ Using refresh_token from sessionStorage (backup token)'
          )
        }
      }

      // 4. Fallback to URL params (may contain old/invalid tokens, use as last resort)
      if (!refreshToken && typeof window !== 'undefined') {
        const currentUrl = new URL(window.location.href)
        const urlParams = currentUrl.searchParams

        // Log all URL params for debugging
        const allParamKeys: string[] = []
        urlParams.forEach((_, key) => {
          allParamKeys.push(key)
        })

        console.log('🔍 Checking URL params for refresh token:', {
          allParams: allParamKeys,
          hasRefreshToken: urlParams.has('refresh_token'),
          refreshTokenValue: urlParams.get('refresh_token')
            ? `${urlParams.get('refresh_token')?.substring(0, 30)}...`
            : 'N/A'
        })

        // Try different possible key names for refresh token
        refreshToken =
          urlParams.get('refresh_token') ||
          urlParams.get('refreshToken') ||
          urlParams.get('refresh-token')

        if (refreshToken) {
          console.log(
            '⚠️ Using refresh_token from URL params (fallback - may be old/invalid)'
          )
        } else {
          console.warn(
            '⚠️ No refresh_token found in URL params. Available params:',
            allParamKeys
          )
        }
      }

      console.log('Access token check:', {
        fromJoinDetails: !!joinDetails?.auth_token,
        fromUrlParams:
          typeof window !== 'undefined'
            ? (() => {
                const url = new URL(window.location.href)
                return !!(
                  url.searchParams.get('access_token') ||
                  url.searchParams.get('auth_token')
                )
              })()
            : false,
        fromLocalStorage:
          typeof window !== 'undefined'
            ? !!(
                localStorage.getItem('access_token') ||
                localStorage.getItem('accessToken') ||
                localStorage.getItem('auth_token') ||
                localStorage.getItem('token')
              )
            : false,
        fromSessionStorage:
          typeof window !== 'undefined'
            ? !!(
                sessionStorage.getItem('access_token') ||
                sessionStorage.getItem('accessToken') ||
                sessionStorage.getItem('auth_token') ||
                sessionStorage.getItem('token')
              )
            : false,
        hasAccessToken: !!authToken,
        accessTokenLength: authToken ? authToken.length : 0,
        tokenSource: authToken
          ? typeof window !== 'undefined'
            ? (() => {
                const url = new URL(window.location.href)
                if (url.searchParams.get('access_token'))
                  return 'URL:access_token'
                if (url.searchParams.get('auth_token')) return 'URL:auth_token'
                if (joinDetails?.auth_token === authToken)
                  return 'joinDetails:auth_token'
                if (localStorage.getItem('access_token') === authToken)
                  return 'localStorage:access_token'
                if (localStorage.getItem('auth_token') === authToken)
                  return 'localStorage:auth_token'
                return 'unknown'
              })()
            : 'unknown'
          : 'none'
      })

      // Also check role from URL params if not already set
      // finalRole is already declared at function scope
      if (typeof window !== 'undefined') {
        const currentUrl = new URL(window.location.href)
        const urlParams = currentUrl.searchParams
        const roleFromUrl = urlParams.get('role')
        if (
          roleFromUrl &&
          (roleFromUrl.toLowerCase() === 'doctor' ||
            roleFromUrl.toLowerCase() === 'patient')
        ) {
          finalRole = roleFromUrl.toLowerCase() as UserRole
          console.log('Using role from URL params:', finalRole)
        }
      }

      console.log('API call condition check:', {
        appointmentId,
        hasAuthToken: !!authToken,
        role: finalRole,
        willCallAPI: !!(appointmentId && authToken && finalRole),
        allConditions: {
          hasAppointmentId: !!appointmentId,
          hasAuthToken: !!authToken,
          hasRole: !!finalRole
        },
        extractedValues: {
          appointmentId,
          authTokenPreview: authToken
            ? `${authToken.substring(0, 20)}...`
            : null,
          role: finalRole
        }
      })

      // AI summary: finalize the doctor's call recording (upload mixed audio + trigger the
      // AI summary generation). Doctor client only, and bounded by a timeout so a slow upload
      // can never hang or break the end-call flow.
      if (aiRecorderRef.current && finalRole === 'doctor') {
        // Fire-and-forget: kick off the AI-summary finalize (final clip upload + mixed audio
        // upload + summary generation) WITHOUT awaiting it, so ending the call is instant.
        // The upload/summary continue in the background — ending the call only changes the
        // in-app screen (it doesn't close the tab), so the in-flight fetches still complete,
        // and the doctor's dedicated mic stays live until finalize finishes on its own.
        const recorder = aiRecorderRef.current
        aiRecorderRef.current = null
        recorder
          .finalize()
          .catch((e) => console.warn('[AI recorder] finalize error:', e))
      }

      // Make API call to end the call if we have appointment_id and access_token
      // The API requires authentication, so we need both appointmentId and accessToken
      // Note: We use access_token (not auth_token) for the Authorization header
      if (appointmentId && finalRole && authToken) {
        const apiBaseUrl =
          process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'
        const endpoint =
          finalRole === 'doctor'
            ? `${apiBaseUrl}/consultation/doctor/appointments/${appointmentId}/end-call`
            : `${apiBaseUrl}/consultation/patient/appointments/${appointmentId}/end-call`

        try {
          // Check if access token is expired before making the API call
          let currentAuthToken = authToken
          let currentRefreshToken = refreshToken

          // Extract session_id from URL params for fetching fresh tokens if needed
          let sessionId: string | null = null
          if (typeof window !== 'undefined') {
            const currentUrl = new URL(window.location.href)
            const urlParams = currentUrl.searchParams
            sessionId = urlParams.get('session_id')
            console.log(
              '🔍 Extracted session_id from URL:',
              sessionId ? `${sessionId.substring(0, 20)}...` : 'N/A'
            )
          }

          // Log token and refresh token availability
          console.log('🔍 Token check before API call:', {
            hasAuthToken: !!currentAuthToken,
            hasRefreshToken: !!currentRefreshToken,
            hasSessionId: !!sessionId,
            role: finalRole,
            authTokenPreview: currentAuthToken
              ? `${currentAuthToken.substring(0, 30)}...`
              : 'N/A',
            refreshTokenPreview: currentRefreshToken
              ? `${currentRefreshToken.substring(0, 30)}...`
              : 'N/A'
          })

          // Try to decode and check token expiration
          let tokenCheck: { payload: any; isExpired: boolean } | null = null
          try {
            if (currentAuthToken) {
              tokenCheck = decodeJWT(currentAuthToken)
              console.log('🔍 Token expiration check:', {
                isExpired: tokenCheck.isExpired,
                hasPayload: !!tokenCheck.payload,
                exp: tokenCheck.payload?.exp,
                currentTime: Math.floor(Date.now() / 1000)
              })
            }
          } catch (decodeError) {
            console.warn('⚠️ Failed to decode JWT token:', decodeError)
            // Continue anyway - will rely on 401 response
          }

          // PRIORITY 1: Try session_id approach FIRST (most reliable)
          // This fetches fresh tokens directly from database
          if (sessionId && tokenCheck?.isExpired) {
            console.log(
              '🔄 Access token is expired, fetching fresh tokens using session_id (PRIMARY APPROACH)...',
              { role: finalRole, hasSessionId: !!sessionId }
            )
            const sessionTokenResult = await fetchTokensBySessionId(sessionId)

            if (sessionTokenResult) {
              console.log(
                '✅ Fresh tokens fetched successfully using session_id'
              )
              currentAuthToken = sessionTokenResult.accessToken
              currentRefreshToken = sessionTokenResult.refreshToken

              // Update tokens in storage
              if (typeof window !== 'undefined') {
                localStorage.setItem('access_token', currentAuthToken)
                localStorage.setItem('refresh_token', currentRefreshToken)
                sessionStorage.setItem('access_token', currentAuthToken)
                sessionStorage.setItem('refresh_token', currentRefreshToken)
              }

              // Update joinDetails
              if (joinDetails) {
                joinDetails.auth_token = currentAuthToken
                joinDetails.refresh_token = currentRefreshToken
              }
            } else {
              console.warn(
                '⚠️ Failed to fetch tokens using session_id. Will try refresh token approach as fallback.'
              )
            }
          }

          // PRIORITY 2: Fallback to refresh token approach if session_id failed or unavailable
          // Note: If session_id approach succeeded, this will be skipped
          if (tokenCheck?.isExpired && currentRefreshToken && !sessionId) {
            console.warn(
              '⚠️ Access token is expired and no session_id available, attempting refresh token approach (FALLBACK)...',
              { role: finalRole, hasRefreshToken: !!currentRefreshToken }
            )
            const tokenResult = await refreshAccessToken(
              currentRefreshToken,
              finalRole
            )

            if (tokenResult) {
              console.log('✅ Token refreshed successfully using refresh token')
              currentAuthToken = tokenResult.accessToken
              // Update refresh token if a new one was provided
              if (tokenResult.refreshToken) {
                currentRefreshToken = tokenResult.refreshToken
                // Also update in joinDetails if available
                if (joinDetails) {
                  joinDetails.refresh_token = tokenResult.refreshToken
                }
              }
            } else {
              console.warn(
                '⚠️ Failed to refresh token. Will attempt API call with original token anyway.'
              )
              // Continue with original token - the API call might still work
              // or we'll handle 401 in the response handler
            }
          } else if (
            tokenCheck?.isExpired &&
            !currentRefreshToken &&
            !sessionId
          ) {
            console.warn(
              '⚠️ Access token is expired but no refresh token or session_id available. Will attempt API call anyway.'
            )
            // Continue with original token - the API call might still work
            // or we'll handle 401 in the response handler
          } else if (tokenCheck && !tokenCheck.isExpired) {
            console.log('✅ Access token is valid, proceeding with API call')
          } else {
            console.log(
              'ℹ️ Token check unavailable, proceeding with API call (will handle 401 if needed)'
            )
          }

          console.log('Calling end call API:', {
            endpoint,
            appointmentId,
            role: finalRole,
            hasAuthToken: !!currentAuthToken,
            tokenExpired: tokenCheck?.isExpired ?? null,
            authTokenPreview: currentAuthToken
              ? `${currentAuthToken.substring(0, 20)}...`
              : 'N/A'
          })

          // Prepare headers with Authorization
          const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${currentAuthToken}`
          }

          const response = await fetch(endpoint, {
            method: 'POST',
            headers
          })

          console.log(
            'End call API response status:',
            response.status,
            response.statusText
          )

          if (!response.ok) {
            const errorText = await response.text()
            let errorData: any = null
            try {
              errorData = JSON.parse(errorText)
            } catch (e) {
              // Error response is not JSON
            }

            // Handle 401 Unauthorized - try to refresh token (fallback if pre-check missed it)
            if (response.status === 401) {
              console.warn('⚠️ Received 401 Unauthorized response')

              // PRIORITY 1: Try to fetch fresh tokens using session_id if available
              if (sessionId) {
                console.warn(
                  '⚠️ Attempting to fetch fresh tokens using session_id (PRIMARY APPROACH)...',
                  {
                    role: finalRole,
                    hasSessionId: !!sessionId,
                    endpoint
                  }
                )

                try {
                  const sessionTokenResult = await fetchTokensBySessionId(
                    sessionId
                  )

                  if (sessionTokenResult) {
                    console.log('✅ Fresh tokens fetched using session_id')
                    currentAuthToken = sessionTokenResult.accessToken
                    currentRefreshToken = sessionTokenResult.refreshToken

                    // Update tokens in storage
                    if (typeof window !== 'undefined') {
                      localStorage.setItem('access_token', currentAuthToken)
                      localStorage.setItem('refresh_token', currentRefreshToken)
                      sessionStorage.setItem('access_token', currentAuthToken)
                      sessionStorage.setItem(
                        'refresh_token',
                        currentRefreshToken
                      )
                    }

                    // Update joinDetails
                    if (joinDetails) {
                      joinDetails.auth_token = currentAuthToken
                      joinDetails.refresh_token = currentRefreshToken
                    }

                    // Retry the API call with fresh tokens
                    console.log(
                      '✅ Retrying end call API with fresh tokens from session...'
                    )
                    const retryResponse = await fetch(endpoint, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${currentAuthToken}`
                      }
                    })

                    if (retryResponse.ok) {
                      const responseText = await retryResponse.text()
                      try {
                        const data = JSON.parse(responseText)
                        console.log(
                          '✅ Call ended successfully on server (after fetching fresh tokens from session):',
                          data
                        )

                        if (data && data.data) {
                          console.log('✅ Appointment updated with:', {
                            status: data.data.status,
                            session_ended_at: data.data.session_ended_at,
                            completed_at: data.data.completed_at,
                            doctor_online: data.data.doctor_online,
                            patient_online: data.data.patient_online
                          })
                        } else {
                          console.warn(
                            '⚠️ Response data structure unexpected:',
                            data
                          )
                        }

                        // Send RTM message to notify other participants that call has ended
                        if (
                          rtmChannelRef.current &&
                          rtmChannelJoinedRef.current
                        ) {
                          try {
                            const endCallMessage = JSON.stringify({
                              type: 'end-call',
                              role: finalRole,
                              timestamp: new Date().toISOString(),
                              appointmentId: appointmentId
                            })

                            await rtmChannelRef.current.sendMessage({
                              text: endCallMessage
                            })

                            console.log(
                              '✅ Sent end-call RTM message to channel (after session token fetch)'
                            )
                          } catch (rtmError) {
                            console.error(
                              '❌ Failed to send end-call RTM message:',
                              rtmError
                            )
                          }
                        }

                        // Success - continue with cleanup below
                      } catch (parseError) {
                        console.error(
                          '❌ Failed to parse retry response as JSON:',
                          parseError
                        )
                      }
                    } else {
                      const retryErrorText = await retryResponse.text()
                      console.error(
                        '❌ Retry failed after fetching fresh tokens from session:',
                        retryResponse.status,
                        retryErrorText
                      )
                    }
                  } else {
                    console.warn(
                      '⚠️ Failed to fetch fresh tokens from session - will try refresh token approach'
                    )
                  }
                } catch (sessionTokensError) {
                  console.error(
                    '❌ Error fetching fresh tokens from session:',
                    sessionTokensError
                  )
                }
              }

              // PRIORITY 2: If session_id approach didn't work, try refresh token approach (FALLBACK)
              if (currentRefreshToken && (!sessionId || !currentAuthToken)) {
                console.warn(
                  '⚠️ Attempting to refresh token using refresh_token (FALLBACK APPROACH)...',
                  {
                    role: finalRole,
                    hasRefreshToken: !!currentRefreshToken,
                    endpoint
                  }
                )
                const tokenResult = await refreshAccessToken(
                  currentRefreshToken,
                  finalRole
                )

                if (tokenResult) {
                  console.log('✅ Token refreshed, retrying end call API...')
                  // Update tokens for retry
                  currentAuthToken = tokenResult.accessToken
                  if (tokenResult.refreshToken) {
                    currentRefreshToken = tokenResult.refreshToken
                  }

                  // Retry the API call with new token
                  const retryResponse = await fetch(endpoint, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${currentAuthToken}`
                    }
                  })

                  if (retryResponse.ok) {
                    const responseText = await retryResponse.text()
                    try {
                      const data = JSON.parse(responseText)
                      console.log(
                        '✅ Call ended successfully on server (after token refresh):',
                        data
                      )

                      if (data && data.data) {
                        console.log('✅ Appointment updated with:', {
                          status: data.data.status,
                          session_ended_at: data.data.session_ended_at,
                          completed_at: data.data.completed_at,
                          doctor_online: data.data.doctor_online,
                          patient_online: data.data.patient_online
                        })
                      } else {
                        console.warn(
                          '⚠️ Response data structure unexpected:',
                          data
                        )
                      }

                      // Send RTM message to notify other participants that call has ended
                      if (
                        rtmChannelRef.current &&
                        rtmChannelJoinedRef.current
                      ) {
                        try {
                          const endCallMessage = JSON.stringify({
                            type: 'end-call',
                            role: finalRole,
                            timestamp: new Date().toISOString(),
                            appointmentId: appointmentId
                          })

                          await rtmChannelRef.current.sendMessage({
                            text: endCallMessage
                          })

                          console.log(
                            '✅ Sent end-call RTM message to channel (after token refresh)'
                          )
                        } catch (rtmError) {
                          console.error(
                            '❌ Failed to send end-call RTM message:',
                            rtmError
                          )
                        }
                      }

                      // Success - continue with cleanup below
                    } catch (parseError) {
                      console.error(
                        '❌ Failed to parse retry response as JSON:',
                        parseError
                      )
                    }
                  } else {
                    const retryErrorText = await retryResponse.text()
                    console.error(
                      '❌ Retry failed after token refresh:',
                      retryResponse.status,
                      retryErrorText
                    )
                  }
                } else {
                  console.warn(
                    '⚠️ Failed to refresh token. Proceeding with ending call locally.',
                    { role: finalRole, endpoint }
                  )
                  console.warn(
                    '⚠️ API call failed but call will still end locally:',
                    response.status,
                    response.statusText
                  )
                  if (errorData) {
                    console.warn('Error details:', errorData)
                    console.warn(
                      'Note: Call ending will proceed locally even though API call failed'
                    )
                  }
                  // Continue with ending the call locally - don't block the UI
                }
              } else if (!currentRefreshToken && !sessionId) {
                // 401 but no refresh token or session_id available
                console.warn(
                  '⚠️ Received 401 but no refresh token or session_id available. Proceeding with ending call locally.',
                  {
                    role: finalRole,
                    endpoint,
                    hasRefreshToken: !!currentRefreshToken,
                    hasSessionId: !!sessionId,
                    errorDetails: errorData
                  }
                )
                // Continue with ending the call locally - don't block the UI
              }
            } else {
              // Other errors - log but don't block call ending
              console.warn(
                '⚠️ API call failed but proceeding with ending call locally:',
                response.status,
                response.statusText,
                errorText
              )
              if (errorData) {
                console.warn('Error details:', errorData)
                // Log for debugging but don't block the call ending
                if (response.status === 401) {
                  console.warn(
                    'Authentication failed - proceeding with local call end'
                  )
                } else if (response.status === 403) {
                  console.warn(
                    'Authorization failed - proceeding with local call end'
                  )
                } else if (response.status === 404) {
                  console.warn(
                    'Appointment not found - proceeding with local call end'
                  )
                }
              }
            }
          } else {
            const responseText = await response.text()
            try {
              const data = JSON.parse(responseText)
              console.log('✅ Call ended successfully on server:', data)

              // Verify the response contains expected data
              if (data && data.data) {
                console.log('✅ Appointment updated with:', {
                  status: data.data.status,
                  session_ended_at: data.data.session_ended_at,
                  completed_at: data.data.completed_at,
                  doctor_online: data.data.doctor_online,
                  patient_online: data.data.patient_online
                })
              } else {
                console.warn('⚠️ Response data structure unexpected:', data)
                console.warn(
                  'Expected response.data but got:',
                  Object.keys(data || {})
                )
              }

              // Send RTM message to notify other participants that call has ended
              // This ensures the other participant's call is automatically disconnected
              if (rtmChannelRef.current && rtmChannelJoinedRef.current) {
                try {
                  const endCallMessage = JSON.stringify({
                    type: 'end-call',
                    role: finalRole, // 'doctor' or 'patient'
                    timestamp: new Date().toISOString(),
                    appointmentId: appointmentId
                  })

                  await rtmChannelRef.current.sendMessage({
                    text: endCallMessage
                  })

                  console.log(
                    '✅ Sent end-call RTM message to channel:',
                    endCallMessage
                  )
                  console.log(
                    '✅ Other participant will be automatically disconnected'
                  )
                } catch (rtmError) {
                  console.error(
                    '❌ Failed to send end-call RTM message:',
                    rtmError
                  )
                  // Continue with local cleanup even if RTM message fails
                }
              } else {
                console.warn(
                  '⚠️ RTM channel not available, other participant will not be notified'
                )
                console.warn('RTM channel ref:', !!rtmChannelRef.current)
                console.warn('RTM channel joined:', rtmChannelJoinedRef.current)
              }
            } catch (parseError) {
              console.error('❌ Failed to parse response as JSON:', parseError)
              console.error('Response text:', responseText)
            }
          }
        } catch (apiError) {
          console.error('❌ Error calling end call API:', apiError)
          console.error('API Error details:', {
            message:
              apiError instanceof Error ? apiError.message : String(apiError),
            stack: apiError instanceof Error ? apiError.stack : undefined
          })
          // Continue with ending the call even if API fails
        }
      } else {
        const missingParams = []
        if (!appointmentId) missingParams.push('appointmentId')
        if (!authToken) missingParams.push('authToken')
        if (!finalRole) missingParams.push('role')

        console.error(
          '❌ Cannot call end call API - missing required parameters:',
          {
            appointmentId,
            hasAuthToken: !!authToken,
            role: finalRole,
            missingFields: missingParams,
            joinDetails: joinDetails
              ? {
                  channel_name: joinDetails.channel_name,
                  appointment_id: joinDetails.appointment_id,
                  has_auth_token: !!joinDetails.auth_token
                }
              : null,
            currentUrl:
              typeof window !== 'undefined' ? window.location.href : 'N/A',
            urlParams:
              typeof window !== 'undefined'
                ? (() => {
                    const params = new URL(window.location.href).searchParams
                    const obj: Record<string, string> = {}
                    params.forEach((value, key) => {
                      obj[key] = value
                    })
                    return obj
                  })()
                : null
          }
        )
        console.error(
          '⚠️ Ending call locally only - appointment details will NOT be saved to database!'
        )
        console.error('💡 To save appointment details, ensure:', {
          '1. appointment_id':
            'Must be in channel_name (appointment_123) or joinDetails.appointment_id',
          '2. auth_token':
            'Must be in URL params, joinDetails, localStorage, or sessionStorage',
          '3. role': 'Must be "doctor" or "patient"'
        })
      }

      // Cleanup RTM connection
      if (rtmChannelRef.current) {
        try {
          await rtmChannelRef.current.leave()
        } catch (error) {
          console.error('Error leaving RTM channel:', error)
        }
        rtmChannelRef.current = null
      }
      if (rtmClientRef.current) {
        try {
          await rtmClientRef.current.logout()
        } catch (error) {
          console.error('Error logging out RTM client:', error)
        }
        rtmClientRef.current = null
      }
    } catch (error) {
      console.error('Error in handleEndCall:', error)
      // Continue with ending the call even if there's an error
    } finally {
      // Clear the countdown timer
      if (countdownTimerRef.current) {
        window.clearInterval(countdownTimerRef.current)
        countdownTimerRef.current = undefined
      }
      setTimeRemaining(null)

      // Always proceed with ending the call UI
      exitFullscreen()
      setVideocall(false)
      setJoinDetails(null)

      // Log all available data for debugging
      console.log('🔍 Redirect Debug Info:', {
        appointmentId,
        hasAuthToken: !!authToken,
        finalRole,
        joinDetails: joinDetails
          ? {
              channel_name: joinDetails.channel_name,
              appointment_id: joinDetails.appointment_id,
              has_auth_token: !!joinDetails.auth_token
            }
          : null,
        currentUrl: typeof window !== 'undefined' ? window.location.href : 'N/A'
      })

      // IMPORTANT: Only redirect for doctors, patients should see "Call ended" message
      // Use a flag to control redirect behavior instead of return statement (avoid unsafe-finally)
      const shouldRedirect = finalRole === 'doctor'

      if (!shouldRedirect) {
        console.log(
          '👤 Patient role detected - showing call ended message (no redirect)'
        )
        setError(
          isRemoteEnd
            ? 'The meeting has ended. The other participant has left the call.'
            : 'The meeting has ended. Thank you for using Tiryaq Consultation.'
        )
        // Reset the guard after a short delay
        setTimeout(() => {
          isEndingCallRef.current = false
        }, 1000)
        // Don't execute redirect logic below
      }

      // Only execute redirect logic for doctors
      if (shouldRedirect) {
        console.log('👨‍⚕️ Doctor role detected - proceeding with redirect logic')

        // Try to get patient_id from URL first (if available)
        let patientIdFromUrl: number | null = null
        if (typeof window !== 'undefined') {
          const currentUrl = new URL(window.location.href)
          const urlParams = currentUrl.searchParams
          const patientIdParam = urlParams.get('patient_id')
          if (patientIdParam) {
            const parsed = parseInt(patientIdParam, 10)
            if (!isNaN(parsed) && parsed > 0) {
              patientIdFromUrl = parsed
              console.log('✅ Found patient_id in URL:', patientIdFromUrl)
            }
          }
        }

        // If we have both appointment_id and patient_id from URL, redirect immediately
        if (appointmentId && patientIdFromUrl) {
          console.log(
            '✅ Redirecting immediately with URL data:',
            appointmentId,
            patientIdFromUrl
          )
          const doctorAppUrl =
            process.env.REACT_APP_DOCTOR_APP_URL || 'http://localhost:3000'
          const endedByParam = callEndedByRef.current || 'doctor'
          window.location.href = `${doctorAppUrl}/online?appointment_id=${appointmentId}&patient_id=${patientIdFromUrl}&call_ended=true&ended_by=${endedByParam}`
        } else if (appointmentId && authToken) {
          // Otherwise, fetch appointment details if we have appointment_id and auth token
          // Show redirecting message instead of error
          setError('Redirecting...')

          // Fetch appointment details and redirect
          const apiBaseUrl =
            process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'
          const appointmentEndpoint = `${apiBaseUrl}/consultation/doctor/appointments/${appointmentId}`

          console.log(
            '🔄 Fetching appointment details for redirect:',
            appointmentId,
            'Role:',
            finalRole,
            'Endpoint:',
            appointmentEndpoint
          )

          // Fetch and redirect
          fetch(appointmentEndpoint, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${authToken}`,
              'Content-Type': 'application/json'
            }
          })
            .then(async (response) => {
              console.log('📡 API Response Status:', response.status)
              if (response.ok) {
                const data = await response.json()
                console.log('📦 API Response Data:', data)

                // Extract patient_id from multiple possible locations
                let patientId: number | null = null

                if (data && data.data) {
                  // Try data.patient_id first (direct field)
                  if (data.data.patient_id) {
                    patientId = data.data.patient_id
                    console.log(
                      '✅ Found patient_id in data.patient_id:',
                      patientId
                    )
                  }
                  // Try data.patient.user_id (nested in patient object)
                  else if (data.data.patient && data.data.patient.user_id) {
                    patientId = data.data.patient.user_id
                    console.log(
                      '✅ Found patient_id in data.patient.user_id:',
                      patientId
                    )
                  }
                  // Try data.patient.id (alternative field)
                  else if (data.data.patient && data.data.patient.id) {
                    patientId = data.data.patient.id
                    console.log(
                      '✅ Found patient_id in data.patient.id:',
                      patientId
                    )
                  }
                }

                if (patientId) {
                  console.log(
                    '✅ Redirecting to online page with patient_id:',
                    patientId
                  )

                  // Redirect to doctor app online page with appointment_id and patient_id
                  const doctorAppUrl =
                    process.env.REACT_APP_DOCTOR_APP_URL ||
                    'http://localhost:3000'

                  const redirectUrl = `${doctorAppUrl}/online?appointment_id=${appointmentId}&patient_id=${patientId}&call_ended=true&ended_by=${
                    callEndedByRef.current || 'doctor'
                  }`
                  console.log('🚀 Redirecting to:', redirectUrl)

                  // Use window.location.href for immediate redirect
                  window.location.href = redirectUrl
                } else {
                  console.error(
                    '❌ Could not extract patient_id from appointment data:',
                    data
                  )
                  // Show error message as fallback
                  setError(
                    isRemoteEnd
                      ? 'The meeting has ended. The other participant has left the call.'
                      : 'The meeting has ended. Thank you for using Tiryaq Consultation.'
                  )
                }
              } else {
                const errorText = await response.text()
                console.error(
                  '❌ Failed to fetch appointment details for redirect:',
                  response.status,
                  errorText
                )
                // Show error message as fallback
                setError(
                  isRemoteEnd
                    ? 'The meeting has ended. The other participant has left the call.'
                    : 'The meeting has ended. Thank you for using Tiryaq Consultation.'
                )
              }
            })
            .catch((error) => {
              console.error(
                '❌ Error fetching appointment details for redirect:',
                error
              )
              // Show error message as fallback
              setError(
                isRemoteEnd
                  ? 'The meeting has ended. The other participant has left the call.'
                  : 'The meeting has ended. Thank you for using Tiryaq Consultation.'
              )
            })
        } else {
          // When appointment_id or auth token is not available, show error message
          console.error('❌ Cannot redirect - missing required data:', {
            hasAppointmentId: !!appointmentId,
            appointmentId,
            hasAuthToken: !!authToken,
            authTokenPreview: authToken
              ? `${authToken.substring(0, 20)}...`
              : 'N/A',
            finalRole,
            joinDetailsAvailable: !!joinDetails
          })
          setError(
            isRemoteEnd
              ? 'The meeting has ended. The other participant has left the call.'
              : 'The meeting has ended. Thank you for using Tiryaq Consultation.'
          )
        }
      } // End of if (shouldRedirect) block

      // Reset the guard after a short delay to allow cleanup
      setTimeout(() => {
        isEndingCallRef.current = false
      }, 1000)
    }
  }

  const toggleFullscreen = async () => {
    if (
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).msFullscreenElement
    ) {
      await exitDomFullscreen()
      setIsFullscreen(false)
    } else {
      await enterDomFullscreen()
      setIsFullscreen(true)
    }
  }

  const tryPinRemote = (
    remoteUid: number | string | undefined,
    retryCount = 0
  ) => {
    if (
      hasPinnedRemoteRef.current ||
      remoteUid === undefined ||
      remoteUid === null
    ) {
      return
    }

    // Validate uid is a valid number or string
    const uid =
      typeof remoteUid === 'number'
        ? remoteUid
        : parseInt(String(remoteUid), 10)
    if (isNaN(uid)) {
      console.warn('Invalid remote UID for pinning:', remoteUid)
      return
    }

    // Check if mediaStore has tracks for this user
    const userMedia = mediaStore[uid]
    if (!userMedia) {
      // Tracks not available yet, retry after a delay (max 5 retries with longer delays)
      if (retryCount < 5) {
        const delay = Math.min(500 * (retryCount + 1), 2000) // Progressive delay: 500ms, 1000ms, 1500ms, 2000ms, 2000ms
        console.log(
          `Tracks not ready for user ${uid}, retrying in ${delay}ms... (${
            retryCount + 1
          }/5)`
        )
        setTimeout(() => {
          tryPinRemote(remoteUid, retryCount + 1)
        }, delay)
      } else {
        console.warn(
          `Failed to pin user ${uid} after ${
            retryCount + 1
          } attempts - tracks not available. Disabling auto-pin.`
        )
      }
      return
    }

    // Verify video track exists and try to check if it's ready
    const videoTrack = userMedia.videoTrack
    if (!videoTrack) {
      if (retryCount < 5) {
        const delay = Math.min(500 * (retryCount + 1), 2000)
        console.log(
          `Video track not available for user ${uid}, retrying in ${delay}ms... (${
            retryCount + 1
          }/5)`
        )
        setTimeout(() => {
          tryPinRemote(remoteUid, retryCount + 1)
        }, delay)
      }
      return
    }

    // Try to verify track is live, but handle errors gracefully
    let isTrackReady = false
    try {
      const mediaStreamTrack = (videoTrack as any).getMediaStreamTrack?.()
      if (mediaStreamTrack) {
        isTrackReady = mediaStreamTrack.readyState === 'live'
      } else {
        // If we can't check readyState, assume it's ready if track exists
        // This is a fallback for tracks that don't expose getMediaStreamTrack
        isTrackReady = true
      }
    } catch (e) {
      // If checking readyState fails, assume track is ready if it exists
      // This prevents errors from blocking the pin operation
      console.warn('Could not verify track readyState, assuming ready:', e)
      isTrackReady = true
    }

    // If track is not ready, retry
    if (!isTrackReady) {
      if (retryCount < 5) {
        const delay = Math.min(500 * (retryCount + 1), 2000)
        console.log(
          `Video track not live for user ${uid}, retrying in ${delay}ms... (${
            retryCount + 1
          }/5)`
        )
        setTimeout(() => {
          tryPinRemote(remoteUid, retryCount + 1)
        }, delay)
        return
      }
    }

    // Wrap dispatch in try-catch to handle any getStats errors gracefully
    try {
      // Use REMOTE_SUB constant (1) which represents remoteTrackState.subbed
      // This is the expected format for Agora UIKit's user-swap action
      const REMOTE_SUB = 1

      // Dispatch with error handling - if getStats fails internally, we'll catch it
      dispatch({
        type: 'user-swap',
        value: [
          {
            uid: uid,
            hasAudio: REMOTE_SUB as any,
            hasVideo: REMOTE_SUB as any
          }
        ]
      })
      hasPinnedRemoteRef.current = true
      console.log('Successfully pinned remote user:', uid)
    } catch (error: any) {
      // Catch any errors from dispatch (including getStats errors)
      // If it's a getStats error, we'll retry after a longer delay
      const isGetStatsError =
        error?.message?.includes('getStats') ||
        error?.message?.includes('MediaStreamTrack')

      if (isGetStatsError && retryCount < 5) {
        const delay = 2000 // Wait 2 seconds before retrying getStats errors
        console.warn(
          `getStats error when pinning user ${uid}, retrying in ${delay}ms... (${
            retryCount + 1
          }/5)`,
          error
        )
        setTimeout(() => {
          tryPinRemote(remoteUid, retryCount + 1)
        }, delay)
      } else {
        console.error('Error pinning remote user (giving up):', error)
        // Don't set hasPinnedRemoteRef to true so user can manually pin if needed
      }
    }
  }

  return (
    <>
      {error && !videocall ? (
        <StatusScreen message={error} />
      ) : videocall && joinDetails ? (
        <div className='app-shell'>
          <header className='app-header'>
            <img className='app-logo' src={logo} alt='Tiryaq' />
            {hasTimeRemaining && timeRemaining && (
              <div
                className='app-timer'
                role='timer'
                title={
                  timeUntilStart
                    ? `The ${
                        consultationDurationMs(joinDetails) / 60000
                      }-minute consultation starts in ${timeUntilStart}; the call ends ${
                        consultationDurationMs(joinDetails) / 60000
                      } minutes after that`
                    : `The consultation ends in ${timeRemaining}`
                }
              >
                <span className='app-timer-dot' />
                {timeUntilStart ? (
                  // Joined before the scheduled start: the slot length (15:00 / 40:00) is held,
                  // and the countdown to the START is shown separately ("ends in" would be wrong).
                  <>
                    <span>{timeRemaining}</span>
                    <span className='app-timer-hint'>
                      session · starts in {timeUntilStart}
                    </span>
                  </>
                ) : (
                  <>
                    <span className='app-timer-label'>Ends in</span>
                    <span>{timeRemaining}</span>
                  </>
                )}
              </div>
            )}
          </header>
          <div className='consultation-container'>
            <div className='video-section'>
              <div className='video-stage'>
                <div
                  ref={stageWrapperRef}
                  className='video-wrapper'
                  onDoubleClick={toggleFullscreen}
                  style={
                    isFullscreen
                      ? {
                          position: 'fixed',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          zIndex: 1000,
                          borderRadius: 0
                        }
                      : {}
                  }
                >
                  {remoteDisconnected && (
                    <div
                      role='status'
                      aria-live='polite'
                      style={{
                        position: 'absolute',
                        top: 12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1200,
                        maxWidth: '90%',
                        padding: '10px 16px',
                        borderRadius: 8,
                        backgroundColor: 'rgba(0,0,0,0.75)',
                        color: '#fff',
                        fontSize: 14,
                        textAlign: 'center',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                      }}
                    >
                      The other participant has disconnected. The call stays
                      open so they can rejoin until the appointment ends.
                    </div>
                  )}
                  <AgoraUIKit
                    styleProps={
                      {
                        UIKitContainer: {
                          ...styles.uikitContainer,
                          height: '100%',
                          width: '100%'
                        },
                        gridVideoContainer: styles.gridVideoContainer,
                        maxViewStyles: styles.maxViewStyles,
                        minViewContainer: styles.minViewContainer,
                        minViewStyles: styles.minViewItem,
                        maxViewContainer: styles.maxViewContainer,
                        localBtnContainer: styles.localBtnContainer,
                        BtnTemplateStyles: styles.btnTemplate,
                        localBtnStyles: {
                          muteLocalAudio: styles.circleBtn,
                          muteLocalVideo: styles.circleBtn,
                          screenshare: styles.circleBtn,
                          switchCamera: styles.circleBtn,
                          endCall: styles.endCallBtn
                        },
                        remoteBtnStyles: {
                          remoteSwap: styles.remoteSwapHidden
                        },
                        iconSize: 18,
                        theme: '#ffffff',
                        // Main tile: never crop or stretch the other participant. With the
                        // adaptive layout the tile itself takes the stream's aspect ratio
                        // (portrait phone -> portrait tile), so 'contain' leaves no black bars
                        // inside it; any spare stage space shows the dark stage background and
                        // holds the self-preview. The self-preview tile also takes its camera's
                        // aspect ratio, so 'cover' fills it without cropping.
                        videoMode: { max: 'contain', min: 'cover' },
                        adaptivePinnedLayout: true,
                        localRole: role,
                        // Real names on the tiles; the badges fall back to the role when unknown.
                        localName:
                          role === 'doctor'
                            ? participantNames.doctor
                            : participantNames.patient,
                        remoteName:
                          role === 'doctor'
                            ? participantNames.patient
                            : participantNames.doctor
                      } as Partial<StylePropInterface>
                    }
                    rtcProps={{
                      appId: joinDetails.agora_app_id,
                      channel: joinDetails.channel_name,
                      uid: joinDetails.uid,
                      token: joinDetails.token,
                      role: 'host',
                      enableScreensharing: true,
                      // This app runs its own RTM session (end-call sync) with the RTM token from
                      // the join link. The kit's built-in RTM logged in a SECOND time with the same
                      // uid and no token, which can never succeed on a token-secured project and,
                      // if it ever did, would kick this app's session out. Names come from props.
                      disableRtm: true
                    }}
                    callbacks={{
                      EndCall: () => {
                        if (handleEndCallRef.current) {
                          handleEndCallRef.current(false)
                        } else {
                          handleEndCall(false)
                        }
                      },
                      'user-left': (user: any, reason: string) => {
                        console.log('User left event:', user?.uid, reason)
                        // A screen-share client leaving just means sharing stopped.
                        if (user?.uid === SCREENSHARE_UID) return
                        // The end is already known to be intentional (End pressed, or the other
                        // side's end-call message arrived): finish the call as before.
                        if (intentionalEndRef.current) {
                          ;(handleEndCallRef.current || handleEndCall)(true)
                          return
                        }
                        // Otherwise it may only be a page refresh or a network drop. Keep the
                        // call open; the remote-disconnect check ends it only if the server
                        // says the consultation is over.
                        setRemoteDisconnected(true)
                      },
                      'user-joined': (user: any) => {
                        if (user?.uid === SCREENSHARE_UID) return
                        console.log('User (re)joined:', user?.uid)
                        setRemoteDisconnected(false)
                      }
                    }}
                  />
                </div>
              </div>
            </div>
            {!isFullscreen && (
              <ChatPanel
                authToken={joinDetails.auth_token}
                getAuthToken={getFreshAuthToken}
                appointmentId={joinDetails.appointment_id}
                role={role}
                otherUserId={
                  joinDetails.other_user_id ??
                  (role === 'doctor'
                    ? joinDetails.patient_user_id
                    : joinDetails.doctor_user_id)
                }
                apiBaseUrl={process.env.REACT_APP_API_BASE_URL}
                selfName={
                  role === 'doctor'
                    ? participantNames.doctor
                    : participantNames.patient
                }
                otherName={
                  role === 'doctor'
                    ? participantNames.patient
                    : participantNames.doctor
                }
              />
            )}
          </div>
        </div>
      ) : null}
    </>
  )
}

const styles = {
  uikitContainer: {
    flex: 1,
    display: 'flex',
    minHeight: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'transparent'
  } as CSSProperties,
  gridVideoContainer: {
    backgroundColor: 'transparent'
  } as CSSProperties,
  maxViewStyles: {
    backgroundColor: 'transparent',
    zIndex: 1,
    boxShadow: 'none'
  } as CSSProperties,
  // Self-preview tile. Its position and size are computed by the kit's adaptive pinned layout
  // (beside the patient's picture when there is room, matching the camera's aspect ratio).
  minViewContainer: {
    zIndex: 110,
    pointerEvents: 'auto'
  } as CSSProperties,
  minViewItem: {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    borderRadius: 12,
    overflow: 'hidden',
    background: '#1a1a1a',
    border: '2px solid rgba(255, 255, 255, 0.2)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
  } as CSSProperties,
  maxViewContainer: {
    flex: 1,
    width: '100%',
    minWidth: 0
  } as CSSProperties,
  localBtnContainer: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'auto',
    maxWidth: 'calc(100% - 32px)',
    boxSizing: 'border-box',
    height: 60,
    bottom: 24,
    zIndex: 150,
    minWidth: 300,
    background: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 999,
    padding: '12px 20px',
    gap: 16,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)'
  } as CSSProperties,
  btnTemplate: {
    width: 48,
    height: 48,
    borderRadius: 999,
    display: 'grid',
    placeItems: 'center',
    background: 'rgba(255, 255, 255, 0.2)',
    border: 'none',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    cursor: 'pointer',
    transition: 'all 0.2s'
  } as CSSProperties,
  circleBtn: {
    width: 48,
    height: 48,
    borderRadius: 999,
    background: 'rgba(255, 255, 255, 0.2)',
    border: 'none'
  } as CSSProperties,
  endCallBtn: {
    width: 48,
    height: 48,
    borderRadius: 999,
    background: '#ef4444',
    border: 'none',
    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)'
  } as CSSProperties,
  remoteSwapHidden: {
    display: 'none'
  } as CSSProperties
}

export default App
